import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { trackEvent } from '../lib/analytics'
import {
  playExpeditionReturnCue,
  playExpeditionSendCue,
  playRecipeCraftCue,
  playVehicleDepartureCue,
} from '../lib/audioEngine'
import { captureSyncError } from '../lib/sentry'
import { gemRewardForAchievement, resolveUnlockedAchievements } from '../game/achievements'
import { translateStatic } from '../i18n/translate'
import type { MessageKey } from '../i18n/messages'
import type { PendingEvent } from '../game/events'
import {
  addItems,
  consumeByDefId,
  consumeForRecipe,
  consumeOne,
  equipmentFromWeaponDef,
  getItemDef,
} from '../game/items'
import { scaledExpeditionDurationMs } from '../game/expeditionPartyDuration'
import {
  createInitialLocationExplorationState,
  expeditionParticipantIds,
  isLocationUnlocked,
  LOCATION_LIST,
  LOCATIONS,
  rollTeamExplorationDeltaPct,
} from '../game/locations'
import { pickDiaryLines } from '../game/loot'
import { lt, normalizeLocalizedBio } from '../game/localized'
import { nextOnboardingStep } from '../game/onboarding'
import {
  DEFAULT_QUEST_STATE,
  DEN_BOSCH_GATEWAY_QUEST_ID,
  initialStarterQuestActive,
  isVehicleQuestComplete,
  mergeQuestRuntimeState,
  migrateOnboardingToQuests,
  reduceCookingSkillQuest,
  reduceCookingSkillQuestAvailability,
  reduceDenBoschGatewayExpeditionLoot,
  reduceDenBoschGatewayStart,
  reduceDenBoschGatewaySyncAvailability,
  reduceHideoutMoveQuest,
  reduceHideoutMoveQuestAvailability,
  incrementUtrechtGatewayRecruit,
  reduceStarterQuest,
  reduceUtrechtGatewayAvailability,
  reduceUtrechtGatewayExpeditionLoot,
  reduceUtrechtGatewayStart,
  reduceVehicleQuest,
  reduceVehicleQuestAvailability,
  tryCompleteUtrechtGateway,
  starterQuestRestrictsExpeditions,
  starterQuestUsesTutorialDuration,
  syncUtrechtGatewayPhaseFromState,
  UTRECHT_GATEWAY_QUEST_ID,
  type QuestProgressEvent,
} from '../game/quests'
import {
  maxConcurrentExpeditions,
  computeCraftDurationMs,
  computeNeedRestoreAmount,
  computeStashNeedRestorePreview,
  canReachStationLevel,
  getInitialBuildGate,
  getPassiveNeedRegenPerMinute,
  getRestoreNeedForWorkstation,
  getUpgradeGateForNextLevel,
  meetsHideoutTierMinimum,
  mergeWorkstationStates,
  newGameWorkstationStates,
  RELOCATION_FROM_TIER,
  WORKSTATION_TOOLS,
} from '../game/hideoutProgression'
import { RECIPES } from '../game/recipes'
import {
  BOOK_SKILL_MAP,
  createStarterSkills,
  ensureSurvivorSkills,
  grantSkillXp,
  isSkillUnlocked,
  SURVIVOR_PROGRESS_SKILL,
  unlockSkill,
} from '../game/skills'
import { resolveExpeditions } from '../game/tick'
import {
  computerLabJobDurationMs,
  computerLabProgrammingXpAmount,
  isComputerPartDefId,
  rollComputerLabTrinketPayout,
} from '../game/computerLabOps'
import {
  computeGenerationCooldownMs,
  getGeneratedItemsCount,
  getWorkstationGenerationConfig,
  isGeneratorWorkstationId,
  pickRandomGeneratedItemDefId,
  pullOneGeneratedItem,
} from '../game/workstationGeneration'
import { WORKSTATIONS } from '../game/workstations'
import { friendlySyncError } from '../lib/syncErrors'
import {
  hasPendingSync,
  hasQueuedSyncActions,
  normalizeRemoteSettings,
  pullState,
  pushChanges,
  queueSyncAction,
  type SyncedGameState,
} from '../lib/sync'
import { useAuthStore } from './useAuthStore'
import { resolveBroadcastRecruitmentStep } from '../game/broadcastStation'
import {
  garageCanFitVehicle,
  scrapYieldForVehicleDef,
  vehicleIsOnActiveExpedition,
} from '../game/garageOps'
import {
  getLodgingRestCapacity,
  getMaxSurvivors,
  resolveLodgingEnergyRegen,
} from '../game/lodging'
import {
  getVehicleDef,
  getVehicleEffectiveStatsForInstance,
  pickRandomVehicleDrop,
  RURAL_SOUTH_VEHICLE_DROPS,
  DEN_BOSCH_VEHICLE_DROPS,
  VEHICLE_DURABILITY_LOSS_MIN,
  VEHICLE_DURABILITY_LOSS_MAX,
  VEHICLE_REPAIR_SCRAP_COST,
  VEHICLE_REPAIR_DURABILITY_PER_SCRAP,
} from '../game/vehicles'
import {
  type CraftJob,
  DEFAULT_GAME_SETTINGS,
  type Expedition,
  type GameSettings,
  type AchievementId,
  type ItemStack,
  type LocationId,
  type LocationExplorationState,
  type Needs,
  type OnboardingStep,
  type TutorialStep,
  type QuestId,
  type QuestRuntimeState,
  type PetInstance,
  type ProgressStats,
  type Survivor,
  type Toast,
  type HideoutTierId,
  type VehicleInstance,
  type WorkstationId,
  type WorkstationRuntimeState,
} from '../game/types'

type GameState = {
  survivors: Survivor[]
  inventory: ItemStack[]
  expeditions: Expedition[]
  craftJobs: CraftJob[]
  pendingEvents: PendingEvent[]
  toasts: Toast[]
  /** Number of expeditions completed (for region-unlock gating) */
  progress: number
  locationExploration: Record<LocationId, LocationExplorationState>
  xp: number
  dailyLastClaimedAt: string | null
  achievementsUnlocked: AchievementId[]
  achievementsClaimed: AchievementId[]
  stats: ProgressStats
  trinketPoints: number
  gems: number
  onboardingStep: OnboardingStep
  tutorialStep: TutorialStep | null
  settings: GameSettings
  lastSyncAt: number | null
  isSyncing: boolean
  syncError: string | null
  /** True once we've successfully pulled from the cloud at least once this
   * session. flushSync refuses to push until this flips so a queued action
   * can never overwrite the cloud with empty/default state on a device that
   * lost its localStorage. */
  hydratedFromCloud: boolean
  hideoutTierId: HideoutTierId
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  quests: QuestRuntimeState
  garageVehicles: VehicleInstance[]
  pets: PetInstance[]
}

type GameActions = {
  tick: (now: number) => void
  startExpedition: (
    survivorIds: string[],
    locationId: LocationId,
    vehicleInstanceIds?: string[],
    opts?: { retryRecruit?: boolean },
  ) => void
  completeTripReport: (
    expeditionId: string,
    discardedDefIds: string[],
    recruitFoundSurvivor: boolean,
    storeFoundVehicle?: boolean,
  ) => void
  repairVehicle: (vehicleInstanceId: string) => void
  scrapVehicle: (vehicleInstanceId: string) => void
  upgradeVehiclePart: (vehicleInstanceId: string, partId: string) => void
  acknowledgeVehicleQuestCompletion: () => void
  recheckVehicleQuestGarage: () => void
  runWorkstation: (workstationId: WorkstationId, survivorId: string) => void
  installComputerLabPart: (slotIndex: number, defId: string | null) => void
  assignComputerLabWorker: (slotIndex: number, survivorId: string | null) => void
  runComputerLabSlot: (slotIndex: number) => void
  gemHealWorkstationNeed: (workstationId: WorkstationId) => void
  claimWorkstationGeneratedItem: (stationId: WorkstationId, defId: string) => void
  claimAllWorkstationGeneratedItems: (stationId: WorkstationId) => void
  useItemFromStash: (defId: string, survivorId: string) => void
  resolveEvent: (survivorId: string, choiceIndex: 0 | 1) => void
  dismissToast: (id: string) => void
  setInventoryLock: (defId: string, locked: boolean) => void
  bulkDiscard: (defIds: string[]) => void
  startCraft: (recipeId: string) => void
  learnSkillFromBook: (survivorId: string, bookDefId: string) => void
  equipWeaponFromStash: (survivorId: string, weaponDefId: string) => void
  unequipWeaponToStash: (survivorId: string) => void
  claimDailyLoginBonus: () => void
  advanceOnboarding: () => void
  advanceTutorial: () => void
  updateSettings: (partial: Partial<GameSettings>) => void
  syncFromCloud: () => Promise<void>
  flushSync: () => Promise<void>
  upgradeWorkstation: (stationId: WorkstationId) => void
  relocateHideout: () => void
  assignSurvivorToWorkstation: (
    stationId: WorkstationId,
    survivorId: string | null,
  ) => void
  addLodgingOccupant: (survivorId: string) => void
  removeLodgingOccupant: (survivorId: string) => void
  acceptBroadcastRecruit: (offerSurvivorId: string) => void
  denyBroadcastRecruit: (offerSurvivorId: string) => void
  installWorkstationTool: (stationId: WorkstationId, toolId: string) => void
  buildWorkstation: (stationId: WorkstationId) => void
  commitLeadSurvivor: (survivor: Survivor) => void
  skipExpeditionHalfTime: (expeditionId: string) => void
  resetProgressToNewGame: () => void
  claimAchievement: (id: AchievementId) => void
  trackQuestProgress: (event: QuestProgressEvent) => void
  acknowledgeStarterQuestCompletion: () => void
  acknowledgeDenBoschGatewayCompletion: () => void
  acknowledgeCookingQuestCompletion: () => void
  acknowledgeHideoutMoveQuestCompletion: () => void
  acknowledgeUtrechtGatewayCompletion: () => void
  startQuest: (questId: QuestId) => void
}

export const LEAD_SURVIVOR_ID = 'lead'

const clamp = (n: number) => Math.max(0, Math.min(100, n))

function normalizeNeeds(raw: Partial<Needs> | undefined): Needs {
  const legacy = raw as Partial<Needs> & { morale?: number }
  return {
    hunger: clamp(legacy.hunger ?? 100),
    thirst: clamp(legacy.thirst ?? 100),
    energy: clamp(legacy.energy ?? legacy.morale ?? 100),
  }
}

const PASSIVE_NEED_REGEN_STEP_MS = 60_000
const WORKSTATION_GEM_HEAL_COST = 3
const WORKSTATION_GEM_HEAL_COOLDOWN_MS = 30 * 60 * 1000

function resolveComputerLabTraining(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  survivors: Survivor[],
  now: number,
): {
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  survivors: Survivor[]
  trinketDelta: number
  inventoryAdditions: ItemStack[]
  changed: boolean
} {
  let changed = false
  let trinketDelta = 0
  let inventoryAdditions: ItemStack[] = []
  const w: Record<WorkstationId, WorkstationRuntimeState> = { ...workstations }
  let surv = survivors.map((s) => ({
    ...s,
    skills: { ...s.skills },
  }))

  const ty = w.training_yard
  if (
    ty?.built &&
    ty.trainingYardSessionEndsAt != null &&
    ty.trainingYardSessionEndsAt <= now &&
    ty.assignedSurvivorId
  ) {
    const sid = ty.assignedSurvivorId
    const i = surv.findIndex((s) => s.id === sid)
    if (i >= 0) {
      surv[i] = {
        ...surv[i],
        skills: grantSkillXp(surv[i].skills, 'endurance', 14),
      }
    }
    w.training_yard = { ...ty, trainingYardSessionEndsAt: null }
    changed = true
  }

  const lab = w.computer_lab
  if (lab?.built) {
    const slots = lab.computerLabSlots ?? [null, null, null, null]
    const workers = lab.computerLabWorkers ?? [null, null, null, null]
    const ends = lab.computerLabJobEndsAt ?? [null, null, null, null]
    const newEnds: [
      number | null,
      number | null,
      number | null,
      number | null,
    ] = [...ends]
    let labMut = false
    for (let i = 0; i < 4; i++) {
      const e = newEnds[i]
      if (e == null || e > now) continue
      const part = slots[i]
      newEnds[i] = null
      labMut = true
      if (!part) continue
      trinketDelta += rollComputerLabTrinketPayout(part)
      if (Math.random() < 0.27) {
        inventoryAdditions = addItems(inventoryAdditions, [
          { defId: 'scrap-electronics', quantity: 1 },
        ])
      }
      const wid = workers[i]
      if (wid) {
        const wi = surv.findIndex((s) => s.id === wid)
        if (wi >= 0) {
          surv[wi] = {
            ...surv[wi],
            skills: grantSkillXp(
              surv[wi].skills,
              'programming',
              computerLabProgrammingXpAmount(part),
            ),
          }
        }
      }
    }
    if (labMut) {
      w.computer_lab = { ...lab, computerLabJobEndsAt: newEnds }
      changed = true
    }
  }

  return {
    workstations: w,
    survivors: surv,
    trinketDelta,
    inventoryAdditions,
    changed,
  }
}

function resolveWorkstationGeneration(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  survivors: Survivor[],
  now: number,
): {
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  changed: boolean
} {
  let changed = false
  const next: Record<WorkstationId, WorkstationRuntimeState> = { ...workstations }
  for (const stationId of Object.keys(workstations) as WorkstationId[]) {
    if (!isGeneratorWorkstationId(stationId)) continue
    const ws = workstations[stationId]
    if (!ws?.built) continue
    if (!ws.assignedSurvivorId) continue
    const assigned = survivors.find((s) => s.id === ws.assignedSurvivorId)
    if (!assigned) continue
    const levelConfig = getWorkstationGenerationConfig(stationId, ws.level)
    if (!levelConfig || levelConfig.itemPool.length === 0) continue

    const cooldownMs = computeGenerationCooldownMs(stationId, ws.level, assigned)
    if (!cooldownMs || cooldownMs <= 0) continue

    const anchor = ws.generationAnchorAt ?? now
    if (anchor > now) continue
    const elapsedMs = now - anchor
    const cycles = Math.floor(elapsedMs / cooldownMs)
    if (cycles <= 0) {
      if (ws.generationAnchorAt == null) {
        next[stationId] = { ...ws, generationAnchorAt: now }
        changed = true
      }
      continue
    }

    const currentClaimableCount = getGeneratedItemsCount(ws)
    const room = Math.max(0, levelConfig.maxClaimableItems - currentClaimableCount)
    const producibleCycles = Math.min(cycles, room)
    let generatedItems = ws.generatedItems
    for (let i = 0; i < producibleCycles; i++) {
      const defId = pickRandomGeneratedItemDefId(stationId, ws.level)
      if (!defId) continue
      generatedItems = addItems(generatedItems, [{ defId, quantity: 1 }])
    }

    const nextAnchor = anchor + cycles * cooldownMs
    next[stationId] = {
      ...ws,
      generatedItems,
      generationAnchorAt: nextAnchor,
    }
    changed = true
  }
  return { workstations: next, changed }
}

function resolvePassiveNeedRegen(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  survivors: Survivor[],
  now: number,
): {
  survivors: Survivor[]
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  changed: boolean
} {
  let changed = false
  let nextSurvivors = survivors
  const nextWorkstations: Record<WorkstationId, WorkstationRuntimeState> = { ...workstations }

  for (const stationId of Object.keys(workstations) as WorkstationId[]) {
    const need = getRestoreNeedForWorkstation(stationId)
    if (!need) continue
    const ws = workstations[stationId]
    if (!ws?.built) continue

    const anchor = ws.needRegenAnchorAt ?? now
    if (anchor > now) continue
    const elapsedMs = now - anchor
    const elapsedMinutes = Math.floor(elapsedMs / PASSIVE_NEED_REGEN_STEP_MS)
    if (elapsedMinutes <= 0) {
      if (ws.needRegenAnchorAt == null) {
        nextWorkstations[stationId] = { ...ws, needRegenAnchorAt: now }
        changed = true
      }
      continue
    }

    const regenPerMinute = getPassiveNeedRegenPerMinute(ws.level)
    const gain = elapsedMinutes * regenPerMinute
    if (gain > 0) {
      nextSurvivors = nextSurvivors.map((survivor) => {
        if (survivor.status === 'OnMission') return survivor
        const nextNeedValue = clamp(survivor.needs[need] + gain)
        if (nextNeedValue === survivor.needs[need]) return survivor
        return {
          ...survivor,
          needs: {
            ...survivor.needs,
            [need]: nextNeedValue,
          },
        }
      })
    }

    nextWorkstations[stationId] = {
      ...ws,
      needRegenAnchorAt: anchor + elapsedMinutes * PASSIVE_NEED_REGEN_STEP_MS,
    }
    changed = true
  }

  return { survivors: nextSurvivors, workstations: nextWorkstations, changed }
}

function resolveGarageVehicleRepair(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  garageVehicles: VehicleInstance[],
  expeditions: Expedition[],
  now: number,
): {
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  garageVehicles: VehicleInstance[]
  changed: boolean
} {
  const ws = workstations.garage
  if (!ws?.built || ws.level < 1) {
    return { workstations, garageVehicles, changed: false }
  }
  // Auto-repair only runs while a survivor is assigned.
  if (!ws.assignedSurvivorId) {
    return { workstations, garageVehicles, changed: false }
  }

  const anchor = ws.garageRepairAnchorAt ?? now
  if (anchor > now) {
    return { workstations, garageVehicles, changed: false }
  }

  const elapsedMs = now - anchor
  const elapsedMinutes = Math.floor(elapsedMs / PASSIVE_NEED_REGEN_STEP_MS)

  // Rate: 2 durability/min baseline +1 per garage level, plus tool bonuses.
  const baseRate = 2 + Math.max(0, Math.floor(ws.level) - 1)
  let toolBonus = 0
  for (const toolId of ws.installedToolIds) {
    const tool = WORKSTATION_TOOLS[toolId]
    if (tool?.workstationId === 'garage') {
      toolBonus += tool.effects.vehicleRepairPerMinuteBonus ?? 0
    }
  }
  const ratePerMinute = baseRate + toolBonus

  if (elapsedMinutes <= 0) {
    if (ws.garageRepairAnchorAt == null) {
      return {
        workstations: {
          ...workstations,
          garage: { ...ws, garageRepairAnchorAt: now },
        },
        garageVehicles,
        changed: true,
      }
    }
    return { workstations, garageVehicles, changed: false }
  }

  const activeVehicleIds = new Set<string>()
  for (const exp of expeditions) {
    if (exp.status !== 'active') continue
    for (const vid of exp.vehicleInstanceIds ?? (exp.vehicleInstanceId ? [exp.vehicleInstanceId] : [])) {
      activeVehicleIds.add(vid)
    }
  }

  const gainPerVehicle = elapsedMinutes * ratePerMinute
  const hasAnyRepairable = garageVehicles.some(
    (v) => !activeVehicleIds.has(v.id) && v.durability < 100,
  )

  const nextVehicles = garageVehicles.map((v) => {
    if (activeVehicleIds.has(v.id)) return v
    if (v.durability >= 100) return v
    const nextDur = Math.max(0, Math.min(100, v.durability + gainPerVehicle))
    if (nextDur === v.durability) return v
    return { ...v, durability: nextDur }
  })

  const nextAnchor = anchor + elapsedMinutes * PASSIVE_NEED_REGEN_STEP_MS
  return {
    workstations: {
      ...workstations,
      garage: { ...ws, garageRepairAnchorAt: nextAnchor },
    },
    garageVehicles: nextVehicles,
    changed: hasAnyRepairable || nextAnchor !== ws.garageRepairAnchorAt,
  }
}

const isGemHealableWorkstation = (
  workstationId: WorkstationId,
): workstationId is 'kitchen' | 'well' | 'infirmary' =>
  workstationId === 'kitchen' ||
  workstationId === 'well' ||
  workstationId === 'infirmary'

/**
 * Supabase only stores active expeditions. Unclaimed returns (`status === 'returned'` + `report`)
 * must be merged back on pull so refresh / cloud hydrate does not wipe pending trip reports.
 */
function mergeExpeditionsPreservingUnclaimedReturns(
  remote: Expedition[],
  previous: Expedition[],
): Expedition[] {
  const remoteIds = new Set(remote.map((e) => e.id))
  const merged = [...remote]
  for (const exp of previous) {
    if (exp.status !== 'returned' || !exp.report) continue
    if (remoteIds.has(exp.id)) continue
    merged.push(exp)
  }
  return merged
}

const MINIMAL_STARTER_INVENTORY: ItemStack[] = [
  { defId: 'food-bread', quantity: 8, locked: false },
  { defId: 'water-bottle-half', quantity: 10, locked: false },
  { defId: 'weapon-pipe', quantity: 1, locked: false },
]

function newGameDefaults(): GameState {
  return {
    survivors: [],
    inventory: MINIMAL_STARTER_INVENTORY.map((s) => ({ ...s })),
    expeditions: [],
    craftJobs: [],
    pendingEvents: [],
    toasts: [],
    progress: 0,
    locationExploration: createInitialLocationExplorationState(),
    xp: 0,
    dailyLastClaimedAt: null,
    achievementsUnlocked: [],
    achievementsClaimed: [],
    stats: {
      totalFoodCollected: 0,
      totalCraftsCompleted: 0,
      denBoschTripsCompleted: 0,
      utrechtTripsCompleted: 0,
      totalExpeditionsCompleted: 0,
      survivorsRecruited: 1,
      vehiclesFound: 0,
      hideoutMoves: 0,
      radioBuilt: false,
      totalWaterCollected: 0,
      totalInjuries: 0,
    },
    trinketPoints: 0,
    gems: 100,
    onboardingStep: 'character_create',
    tutorialStep: null,
    settings: { ...DEFAULT_GAME_SETTINGS },
    lastSyncAt: null,
    isSyncing: false,
    syncError: null,
    hydratedFromCloud: false,
    hideoutTierId: 'forest_hideout',
    workstations: newGameWorkstationStates(),
    quests: { ...DEFAULT_QUEST_STATE },
    garageVehicles: [],
    pets: [],
  }
}

const initialState: GameState = newGameDefaults()

let expeditionCounter = 0
const nextExpeditionId = (now: number) => {
  expeditionCounter += 1
  return `exp-${now.toString(36)}-${expeditionCounter}`
}

const ensureSurvivorShape = (raw: Partial<Survivor> & { id: string }): Survivor => {
  const leadFallback: Survivor | undefined =
    raw.id === LEAD_SURVIVOR_ID
      ? {
          id: LEAD_SURVIVOR_ID,
          name: 'Survivor',
          role: 'Scavenger',
          status: 'Idle',
          needs: { hunger: 100, thirst: 100, energy: 100 },
          skills: createStarterSkills(),
          loadout: { weapon: null, tool: null },
          bio: lt('', ''),
        }
      : undefined
  const base = leadFallback
  const hasActiveTimedInjury =
    raw.status === 'Injured' &&
    typeof raw.injuryEndsAt === 'number' &&
    raw.injuryEndsAt > Date.now()
  return {
    id: raw.id,
    name: raw.name ?? base?.name ?? raw.id,
    role: raw.role ?? base?.role ?? 'Scavenger',
    status:
      raw.status === 'OnMission'
        ? 'Idle'
        : raw.status === 'Injured' && !hasActiveTimedInjury
          ? 'Idle'
          : raw.status ?? 'Idle',
    injuryEndsAt: hasActiveTimedInjury ? raw.injuryEndsAt : null,
    needs: normalizeNeeds(raw.needs ?? base?.needs),
    skills: ensureSurvivorSkills(raw.skills ?? base?.skills),
    loadout: raw.loadout ?? base?.loadout ?? { weapon: null, tool: null },
    bio: normalizeLocalizedBio(raw.bio, base?.bio ?? lt('', '')),
    gender: raw.gender ?? base?.gender,
    portrait: raw.portrait ?? base?.portrait,
  }
}

const ensureVehicleInstanceShape = (
  raw: Partial<VehicleInstance> & { id: string; defId: string },
): VehicleInstance => {
  const def = getVehicleDef(raw.defId)

  const clampPartLevel = (n: unknown): 1 | 2 | 3 => {
    const v = typeof n === 'number' ? n : 1
    const f = Math.floor(v)
    if (f <= 1) return 1
    if (f === 2) return 2
    return 3
  }

  const durabilityRaw = typeof raw.durability === 'number' ? raw.durability : 0
  const durability = Math.max(0, Math.min(100, Math.round(durabilityRaw)))

  const partLevels: VehicleInstance['partLevels'] = def
    ? Object.fromEntries(
        def.parts.map((p) => [p.id, clampPartLevel((raw.partLevels ?? {})[p.id])]),
      ) as VehicleInstance['partLevels']
    : ({} as VehicleInstance['partLevels'])

  return {
    id: raw.id,
    defId: raw.defId,
    durability,
    partLevels,
  }
}

const toSyncedState = (state: GameState): SyncedGameState => ({
  survivors: state.survivors,
  inventory: state.inventory,
  expeditions: state.expeditions.filter((exp) => exp.status === 'active'),
  pendingEvents: state.pendingEvents,
  progress: state.progress,
  locationExploration: state.locationExploration,
  xp: state.xp,
  trinketPoints: state.trinketPoints,
  onboardingStep: state.onboardingStep,
  settings: state.settings,
  hideoutTierId: state.hideoutTierId,
  workstations: state.workstations,
  quests: state.quests,
  garageVehicles: state.garageVehicles,
  gems: state.gems,
  pets: state.pets,
})

const inventoryFromOldResources = (
  resources: Partial<Record<string, number>> | undefined,
): ItemStack[] => {
  if (!resources) return MINIMAL_STARTER_INVENTORY.map((s) => ({ ...s }))
  const out: ItemStack[] = []
  if (resources.food)
    out.push({ defId: 'food-bread', quantity: resources.food, locked: false })
  if (resources.water)
    out.push({ defId: 'water-bottle-half', quantity: resources.water, locked: false })
  if (resources.meds)
    out.push({ defId: 'meds-paracetamol', quantity: resources.meds, locked: false })
  if (resources.scrap)
    out.push({ defId: 'scrap-rusty', quantity: resources.scrap, locked: false })
  return out.length > 0 ? out : MINIMAL_STARTER_INVENTORY.map((s) => ({ ...s }))
}

let craftCounter = 0
const nextCraftJobId = (now: number) => {
  craftCounter += 1
  return `craft-${now.toString(36)}-${craftCounter}`
}

let vehicleCounter = 0
const nextVehicleInstanceId = (now: number) => {
  vehicleCounter += 1
  return `vehicle-${now.toString(36)}-${vehicleCounter}`
}

export const useGameStore = create<GameState & GameActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      tick: (now) => {
        const state0 = get()
        const utrechtPhaseSynced = syncUtrechtGatewayPhaseFromState(
          state0.quests,
          state0.inventory,
          state0.workstations,
        )
        if (utrechtPhaseSynced) {
          set({ quests: utrechtPhaseSynced })
        }
        const state = get()
        const dueCraftJobs = state.craftJobs.filter((job) => job.endsAt <= now)
        const hasDueExpeditions = state.expeditions.some(
          (exp) => exp.status === 'active' && exp.endsAt <= now,
        )
        let healedTimedInjury = false
        const survivorsForTick: Survivor[] = state.survivors.map((survivor): Survivor => {
          if (survivor.status !== 'Injured') return survivor
          if (typeof survivor.injuryEndsAt !== 'number' || survivor.injuryEndsAt <= now) {
            healedTimedInjury = true
            return { ...survivor, status: 'Idle', injuryEndsAt: null }
          }
          return survivor
        })
        const passiveNeedRegen = resolvePassiveNeedRegen(
          state.workstations,
          survivorsForTick,
          now,
        )
        const lodgingEnergy = resolveLodgingEnergyRegen(
          passiveNeedRegen.workstations,
          passiveNeedRegen.survivors,
          now,
        )
        const garageRepair = resolveGarageVehicleRepair(
          lodgingEnergy.workstations,
          state.garageVehicles,
          state.expeditions,
          now,
        )
        const generation = resolveWorkstationGeneration(
          garageRepair.workstations,
          lodgingEnergy.survivors,
          now,
        )
        const broadcastStep = resolveBroadcastRecruitmentStep(
          generation.workstations,
          lodgingEnergy.survivors,
          now,
        )
        const labTraining = resolveComputerLabTraining(
          broadcastStep.workstations,
          lodgingEnergy.survivors,
          now,
        )
        if (
          !hasDueExpeditions &&
          dueCraftJobs.length === 0 &&
          !healedTimedInjury &&
          !passiveNeedRegen.changed &&
          !lodgingEnergy.changed &&
          !garageRepair.changed &&
          !generation.changed &&
          !broadcastStep.changed &&
          !labTraining.changed
        ) {
          return
        }
        const result = resolveExpeditions({
          survivors: labTraining.survivors,
          expeditions: state.expeditions,
          garageVehicles: garageRepair.garageVehicles,
          now,
        })
        let inventory = addItems(state.inventory, labTraining.inventoryAdditions)
        const craftToasts: Toast[] = []
        const stats: ProgressStats = { ...state.stats }
        let survivors = result.survivors.map((survivor) => ({
          ...survivor,
          skills: { ...survivor.skills },
        }))
        const resolvedIds = new Set(Object.keys(result.reports))
        for (const expedition of state.expeditions.filter((exp) => resolvedIds.has(exp.id))) {
          for (const pid of expeditionParticipantIds(expedition)) {
            const idx = survivors.findIndex((s) => s.id === pid)
            if (idx < 0) continue
            const current = survivors[idx]
            survivors[idx] = {
              ...current,
              skills: grantSkillXp(
                grantSkillXp(
                  grantSkillXp(
                    grantSkillXp(current.skills, 'looting', 8),
                    'stealth',
                    6,
                  ),
                  'endurance',
                  4,
                ),
                SURVIVOR_PROGRESS_SKILL,
                18,
              ),
            }
          }
          if (LOCATIONS[expedition.locationId].region === 'den-bosch') {
            stats.denBoschTripsCompleted += 1
          }
          if (LOCATIONS[expedition.locationId].region === 'utrecht') {
            stats.utrechtTripsCompleted += 1
          }
          stats.totalExpeditionsCompleted += 1
          if (expedition.report?.injured) {
            stats.totalInjuries += 1
          }
        }
        stats.totalFoodCollected += Object.values(result.reports).reduce((sum, report) => {
          return (
            sum +
            report.collectedItems.reduce((inner, stack) => {
              const def = getItemDef(stack.defId)
              return def?.category === 'food' ? inner + stack.quantity : inner
            }, 0)
          )
        }, 0)
        stats.totalWaterCollected += Object.values(result.reports).reduce((sum, report) => {
          return (
            sum +
            report.collectedItems.reduce((inner, stack) => {
              const def = getItemDef(stack.defId)
              return def?.category === 'water' ? inner + stack.quantity : inner
            }, 0)
          )
        }, 0)
        if (dueCraftJobs.length > 0) {
          for (const job of dueCraftJobs) {
            const recipe = RECIPES[job.recipeId]
            if (!recipe) continue
            if (recipe.output.type === 'item') {
              inventory = addItems(inventory, [
                { defId: recipe.output.defId, quantity: recipe.output.quantity },
              ])
              craftToasts.push({
                id: `toast-craft-${job.id}`,
                survivorName: recipe.workstationId,
                delivered: [
                  { defId: recipe.output.defId, quantity: recipe.output.quantity },
                ],
              })
              trackEvent('recipe_crafted', { recipe_id: job.recipeId })
              playRecipeCraftCue(state.settings.soundEnabled)
              if (
                job.recipeId === 'workshop-radio' ||
                job.recipeId === 'workshop-radio-repair'
              ) {
                stats.radioBuilt = true
              }
            }
            stats.totalCraftsCompleted += 1
            const xpGrant = recipe.skillXp
            const crafterId = job.assignedSurvivorId
            if (xpGrant && crafterId) {
              const idx = survivors.findIndex((s) => s.id === crafterId)
              if (idx >= 0) {
                const cur = survivors[idx]
                survivors[idx] = {
                  ...cur,
                  skills: grantSkillXp(
                    cur.skills,
                    xpGrant.skill,
                    xpGrant.amount,
                  ),
                }
              }
            }
          }
        }
        const nextAchievements = resolveUnlockedAchievements(
          stats,
          state.achievementsUnlocked,
        )
        if (nextAchievements.length > state.achievementsUnlocked.length) {
          const newlyUnlocked = nextAchievements.filter(
            (id) => !state.achievementsUnlocked.includes(id),
          )
          for (const achievementId of newlyUnlocked) {
            craftToasts.push({
              id: `toast-achievement-${achievementId}-${now}`,
              survivorName: 'shelter',
              message: {
                nl: translateStatic('nl', 'milestone_unlocked_toast' as MessageKey, {
                  name: translateStatic('nl', `milestone_${achievementId}_title` as MessageKey),
                }),
                en: translateStatic('en', 'milestone_unlocked_toast' as MessageKey, {
                  name: translateStatic('en', `milestone_${achievementId}_title` as MessageKey),
                }),
              },
              delivered: [],
            })
          }
        }
        if (result.resolvedCount > 0) {
          playExpeditionReturnCue(state.settings.soundEnabled)
          trackEvent('expedition_completed', {
            count: String(result.resolvedCount),
          })
        }

        if (
          result.resolvedCount === 0 &&
          craftToasts.length === 0 &&
          !healedTimedInjury &&
          !passiveNeedRegen.changed &&
          !lodgingEnergy.changed &&
          !garageRepair.changed &&
          !generation.changed &&
          !broadcastStep.changed &&
          !labTraining.changed
        ) {
          return
        }
        queueSyncAction('tickResolved')
        set((prev) => {
          const expeditionsOut = result.expeditions
          const nextProgress = prev.progress + result.resolvedCount
          const nextExploration: Record<LocationId, LocationExplorationState> = {
            ...createInitialLocationExplorationState(),
            ...prev.locationExploration,
          }
          let questsNext = mergeQuestRuntimeState(prev.quests)
          let questsDirty = false

          if (result.resolvedCount > 0) {
            const qr = reduceStarterQuest(questsNext, {
              type: 'expedition_returned',
              resolvedCount: result.resolvedCount,
            })
            if (qr) {
              questsNext = qr
              questsDirty = true
            }

            for (const expedition of expeditionsOut) {
              if (
                expedition.status !== 'returned' ||
                !expedition.report ||
                !resolvedIds.has(expedition.id)
              ) {
                continue
              }
              const location = LOCATIONS[expedition.locationId]
              const stateForLocation =
                nextExploration[expedition.locationId] ??
                createInitialLocationExplorationState()[expedition.locationId]
              const beforeRaw = clamp(stateForLocation.progressPct)
              const teamSize = expeditionParticipantIds(expedition).length
              const gain = rollTeamExplorationDeltaPct(
                location.exploration.progressGain,
                teamSize,
              )
              const afterRaw = clamp(beforeRaw + gain)
              const beforePctRounded = Math.round(beforeRaw)
              const afterPctRounded = Math.round(afterRaw)

              const claimed = new Set(stateForLocation.claimedRewardThresholds)
              let explorationRewards = expedition.report.explorationRewards ?? []
              for (const tier of location.exploration.tierRewards) {
                const crossed =
                  beforePctRounded < tier.thresholdPct &&
                  afterPctRounded >= tier.thresholdPct
                if (!crossed || claimed.has(tier.thresholdPct)) continue
                claimed.add(tier.thresholdPct)
                explorationRewards = addItems(explorationRewards, tier.guaranteedLoot)
              }

              let foundSurvivor = expedition.report.foundSurvivor
              let recruitOffered = stateForLocation.recruitOffered
              const recruitDef = location.exploration.recruit
              // Offer once exploration reaches the threshold (not only when this trip crosses it).
              // Otherwise saves already at e.g. 100% never trigger (before/after both ≥ atPct).
              if (
                recruitDef &&
                !recruitOffered &&
                afterPctRounded >= recruitDef.atPct &&
                !survivors.some((s) => s.id === recruitDef.survivor.id)
              ) {
                foundSurvivor = { ...recruitDef.survivor }
                recruitOffered = true
              }

              const mergedLoot = addItems(
                expedition.report.collectedItems,
                explorationRewards,
              )

              const qLoot = reduceDenBoschGatewayExpeditionLoot(
                questsNext,
                expedition.locationId,
                mergedLoot,
              )
              if (qLoot) {
                questsNext = qLoot
                questsDirty = true
              }

              const uLoot = reduceUtrechtGatewayExpeditionLoot(
                questsNext,
                mergedLoot,
                labTraining.workstations,
              )
              if (uLoot) {
                questsNext = uLoot
                questsDirty = true
              }

              // Vehicle quest: phase 1 abandoned-farm → guaranteed bike drop
              let foundVehicleDefId: string | undefined
              const vq = questsNext.vehicleQuest
              if (
                vq.status === 'active' &&
                vq.phaseIndex === 1 &&
                expedition.locationId === 'abandoned-farm'
              ) {
                foundVehicleDefId = 'bike-city'
                const qVeh = reduceVehicleQuest(questsNext, {
                  type: 'expedition_returned_from',
                  locationId: expedition.locationId,
                })
                if (qVeh) {
                  questsNext = qVeh
                  questsDirty = true
                }
              } else if (isVehicleQuestComplete(questsNext)) {
                // Random super-rare vehicle drops after quest complete
                const region = LOCATIONS[expedition.locationId].region
                const drops =
                  region === 'den-bosch' || region === 'utrecht'
                    ? DEN_BOSCH_VEHICLE_DROPS
                    : RURAL_SOUTH_VEHICLE_DROPS
                const randomDrop = pickRandomVehicleDrop(drops)
                if (randomDrop) {
                  foundVehicleDefId = randomDrop
                }
              }

              // Hideout move quest: phase 0 - trip to abandoned farm
              const hmq = questsNext.hideoutMoveQuest
              if (
                hmq.status === 'active' &&
                hmq.phaseIndex === 0 &&
                expedition.locationId === 'abandoned-farm'
              ) {
                const qHm = reduceHideoutMoveQuest(questsNext, {
                  type: 'expedition_returned_from',
                  locationId: expedition.locationId,
                })
                if (qHm) {
                  questsNext = qHm
                  questsDirty = true
                }
              }

              const explorationForUnlock = {
                ...nextExploration,
                [expedition.locationId]: {
                  ...stateForLocation,
                  progressPct: afterRaw,
                  claimedRewardThresholds: [...claimed].sort((a, b) => a - b),
                  recruitOffered,
                },
              }

              const unlockedLocationIds = LOCATION_LIST.filter(
                (candidate) =>
                  candidate.unlockRequirement?.locationId === expedition.locationId &&
                  beforePctRounded < candidate.unlockRequirement.explorationPct &&
                  afterPctRounded >= candidate.unlockRequirement.explorationPct &&
                  isLocationUnlocked(
                    candidate.id,
                    nextProgress,
                    explorationForUnlock,
                    questsNext,
                  ),
              ).map((candidate) => candidate.id)

              nextExploration[expedition.locationId] = {
                progressPct: afterRaw,
                claimedRewardThresholds: [...claimed].sort((a, b) => a - b),
                recruitOffered,
              }

              expedition.report = {
                ...expedition.report,
                collectedItems: mergedLoot,
                foundSurvivor,
                explorationBeforePct: beforeRaw,
                explorationAfterPct: afterRaw,
                explorationDeltaPct: afterRaw - beforeRaw,
                explorationRewards,
                unlockedLocationIds,
                foundVehicleDefId,
              }
            }
          }

          const qAvail = reduceDenBoschGatewaySyncAvailability(
            questsNext,
            nextProgress,
            nextExploration,
          )
          if (qAvail) {
            questsNext = qAvail
            questsDirty = true
          }

          const qUtrechtAvail = reduceUtrechtGatewayAvailability(
            questsNext,
            nextExploration,
          )
          if (qUtrechtAvail) {
            questsNext = qUtrechtAvail
            questsDirty = true
          }

          const qVehAvail = reduceVehicleQuestAvailability(
            questsNext,
            prev.workstations.garage?.built === true,
          )
          if (qVehAvail) {
            questsNext = qVehAvail
            questsDirty = true
          }

          const qHideoutMoveAvail = reduceHideoutMoveQuestAvailability(
            questsNext,
            prev.hideoutTierId,
            prev.workstations.base_station?.level ?? 1,
          )
          if (qHideoutMoveAvail) {
            questsNext = qHideoutMoveAvail
            questsDirty = true
          }

          if (questsDirty) queueSyncAction('questsUpdated')

          return {
            survivors,
            inventory,
            expeditions: expeditionsOut,
            craftJobs: prev.craftJobs.filter((job) => job.endsAt > now),
            pendingEvents: prev.pendingEvents,
            toasts: [...prev.toasts, ...craftToasts],
            progress: nextProgress,
            locationExploration: nextExploration,
            stats,
            achievementsUnlocked: nextAchievements,
            quests: questsNext,
            garageVehicles: garageRepair.garageVehicles,
            workstations: labTraining.workstations,
            trinketPoints: prev.trinketPoints + labTraining.trinketDelta,
          }
        })
      },

      startExpedition: (survivorIds, locationId, vehicleInstanceIds, opts) => {
        const state = get()
        const idsOrdered = survivorIds.filter(Boolean)
        const uniqueIds = [...new Set(idsOrdered)]
        if (uniqueIds.length === 0) return
        if (uniqueIds.length !== idsOrdered.length) return

        if (
          !isLocationUnlocked(
            locationId,
            state.progress,
            state.locationExploration,
            state.quests,
          )
        ) {
          return
        }
        if (
          starterQuestRestrictsExpeditions(state.quests) &&
          locationId !== 'vinkel-bridge'
        ) {
          return
        }
        const expeditionLimit = maxConcurrentExpeditions(state.hideoutTierId)
        const activeExpeditions = state.expeditions.filter(
          (exp) => exp.status === 'active',
        ).length
        if (activeExpeditions >= expeditionLimit) {
          set((prev) => ({
            toasts: [
              ...prev.toasts,
              {
                id: `toast-exp-limit-${Date.now()}`,
                survivorName: 'shelter',
                message: lt(
                  `Je kunt maximaal ${expeditionLimit} tochten tegelijk lopen. Wacht tot er iemand terug is.`,
                  `You can run at most ${expeditionLimit} trips at once. Wait for someone to return.`,
                ),
                delivered: [],
              },
            ],
          }))
          return
        }

        for (const id of uniqueIds) {
          const survivor = state.survivors.find((s) => s.id === id)
          if (!survivor || survivor.status !== 'Idle') return
        }

        const leaderId = uniqueIds[0]
        const loc = LOCATIONS[locationId]
        const now = Date.now()
        const baseDurationMs = starterQuestUsesTutorialDuration(state.quests)
          ? 20_000
          : loc.durationMs
        const resolvedVehicleInstances = (vehicleInstanceIds ?? [])
          .map((id) => state.garageVehicles.find((v) => v.id === id))
          .filter((v): v is NonNullable<typeof v> => v != null)
        const primaryVehicle = resolvedVehicleInstances[0]
        const vehicleDurationMultiplier =
          resolvedVehicleInstances.length > 0
            ? resolvedVehicleInstances.reduce((sum, v) => {
                return sum + getVehicleEffectiveStatsForInstance(v).durationMultiplier
              }, 0) / resolvedVehicleInstances.length
            : 1
        const expeditionDurationMs = scaledExpeditionDurationMs(
          baseDurationMs,
          uniqueIds.length,
          vehicleDurationMultiplier,
        )
        const expedition: Expedition = {
          id: nextExpeditionId(now),
          locationId,
          survivorIds: uniqueIds,
          survivorId: leaderId,
          startedAt: now,
          endsAt: now + expeditionDurationMs,
          status: 'active',
          diary: pickDiaryLines(locationId, get().settings.language),
          vehicleInstanceId: primaryVehicle?.id,
          vehicleDefId: primaryVehicle?.defId,
          vehicleInstanceIds: resolvedVehicleInstances.map((v) => v.id),
          vehicleDefIds: resolvedVehicleInstances.map((v) => v.defId),
        }
        let questsNext = state.quests
        const qr = reduceStarterQuest(state.quests, {
          type: 'expedition_started',
          locationId,
        })
        if (qr) {
          questsNext = qr
          queueSyncAction('questsUpdated')
        }
        queueSyncAction('startExpedition')
        const onMission = new Set(uniqueIds)
        const lodgingWs = state.workstations.lodging
        const lodgingOccupants = lodgingWs?.lodgingOccupantIds ?? []
        const strippedLodging =
          lodgingOccupants.length > 0
            ? lodgingOccupants.filter((id) => !onMission.has(id))
            : lodgingOccupants
        const workstationsNext =
          lodgingOccupants.length !== strippedLodging.length && lodgingWs
            ? {
                ...state.workstations,
                lodging: { ...lodgingWs, lodgingOccupantIds: strippedLodging },
              }
            : state.workstations

        let locationExplorationNext = state.locationExploration
        if (opts?.retryRecruit) {
          const locDef = LOCATIONS[locationId]
          const recruitDef = locDef.exploration.recruit
          if (recruitDef) {
            const recruitId = recruitDef.survivor.id
            const alreadyRecruited = state.survivors.some((s) => s.id === recruitId)
            const exState =
              state.locationExploration[locationId] ??
              createInitialLocationExplorationState()[locationId]
            if (!alreadyRecruited && exState.recruitOffered) {
              locationExplorationNext = {
                ...state.locationExploration,
                [locationId]: { ...exState, recruitOffered: false },
              }
            }
          }
        }

        set({
          expeditions: [...state.expeditions, expedition],
          survivors: state.survivors.map((s) =>
            onMission.has(s.id)
              ? { ...s, status: 'OnMission', injuryEndsAt: null }
              : s,
          ),
          workstations: workstationsNext,
          quests: questsNext,
          locationExploration: locationExplorationNext,
        })
        const soundEnabled = get().settings.soundEnabled
        if (primaryVehicle?.defId) {
          const vDef = getVehicleDef(primaryVehicle.defId)
          if (vDef) {
            playVehicleDepartureCue(soundEnabled, vDef.audioKind)
          } else {
            playExpeditionSendCue(soundEnabled)
          }
        } else {
          playExpeditionSendCue(soundEnabled)
        }
        trackEvent('expedition_started', { location_id: locationId })
      },

      completeTripReport: (
        expeditionId,
        discardedDefIds,
        recruitFoundSurvivor,
        storeFoundVehicle = false,
      ) => {
        const discarded = new Set(discardedDefIds)
        queueSyncAction('inventoryUpdated')
        queueSyncAction('tickResolved')
        queueSyncAction('questsUpdated')
        set((prev) => {
          const expedition = prev.expeditions.find((entry) => entry.id === expeditionId)
          if (!expedition || expedition.status !== 'returned' || !expedition.report) {
            return prev
          }
          const keptLoot = expedition.report.collectedItems.filter(
            (stack) => !discarded.has(stack.defId),
          )
          let inventory = addItems(prev.inventory, keptLoot)
          let pets = prev.pets
          const strayPetQty = keptLoot
            .filter((s) => s.defId === 'pet-companion-stray')
            .reduce((n, s) => n + s.quantity, 0)
          if (strayPetQty > 0) {
            const consumedPet = consumeByDefId(inventory, 'pet-companion-stray', 1)
            if (consumedPet) {
              inventory = consumedPet
              pets = [
                ...pets,
                {
                  id: `pet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
                  species: 'dog',
                  bond: 15,
                },
              ]
            }
          }
          let survivors = prev.survivors
          const found = expedition.report.foundSurvivor
          const rosterCap = getMaxSurvivors(prev.workstations)
          if (
            found &&
            recruitFoundSurvivor &&
            survivors.length < rosterCap &&
            !survivors.some((s) => s.id === found.id)
          ) {
            survivors = [...survivors, { ...found, status: 'Idle' }]
          }

          // Deduct vehicle durability for all vehicles on this expedition
          let garageVehicles = prev.garageVehicles
          const vehicleIdsToDegrade = expedition.vehicleInstanceIds?.length
            ? expedition.vehicleInstanceIds
            : expedition.vehicleInstanceId
              ? [expedition.vehicleInstanceId]
              : []
          if (vehicleIdsToDegrade.length > 0) {
            const lossById: Record<string, number> = {}
            for (const vid of vehicleIdsToDegrade) {
              const vehicle = prev.garageVehicles.find((v) => v.id === vid)
              const wearMultiplier = vehicle
                ? getVehicleEffectiveStatsForInstance(vehicle).durabilityLossMultiplier
                : 1
              const rawLoss =
                VEHICLE_DURABILITY_LOSS_MIN +
                Math.floor(
                  Math.random() *
                    (VEHICLE_DURABILITY_LOSS_MAX - VEHICLE_DURABILITY_LOSS_MIN + 1),
                )
              lossById[vid] = Math.max(
                0,
                Math.round(rawLoss * (wearMultiplier ?? 1)),
              )
            }
            garageVehicles = prev.garageVehicles.map((v) =>
              lossById[v.id] !== undefined
                ? { ...v, durability: Math.max(0, v.durability - lossById[v.id]) }
                : v,
            )
          }

          // Store a newly found vehicle in the garage (slot capacity by garage level)
          let stats = { ...prev.stats }
          const garageWs = prev.workstations.garage
          const garageLevel =
            garageWs?.built === true ? Math.max(1, garageWs.level) : 0
          if (
            storeFoundVehicle &&
            expedition.report.foundVehicleDefId &&
            garageCanFitVehicle(
              garageLevel,
              garageVehicles,
              expedition.report.foundVehicleDefId,
            )
          ) {
            const now = Date.now()
            const foundDef = getVehicleDef(expedition.report.foundVehicleDefId)
            const partLevels = foundDef
              ? Object.fromEntries(foundDef.parts.map((p) => [p.id, 1])) as VehicleInstance['partLevels']
              : ({} as VehicleInstance['partLevels'])
            const newVehicle: VehicleInstance = {
              id: nextVehicleInstanceId(now),
              defId: expedition.report.foundVehicleDefId,
              durability: 100,
              partLevels,
            }
            garageVehicles = [...garageVehicles, newVehicle]
            stats.vehiclesFound += 1
          }

          // Track survivor recruitment
          const recruitingNow =
            found &&
            recruitFoundSurvivor &&
            !prev.survivors.some((s) => s.id === found.id)
          if (recruitingNow) {
            stats.survivorsRecruited = survivors.length
          }

          const nextAchievements = resolveUnlockedAchievements(stats, prev.achievementsUnlocked)

          // Activate cooking skill quest when 3rd survivor is recruited
          let quests = prev.quests
          const cookingActivation = reduceCookingSkillQuestAvailability(
            quests,
            survivors.length,
            inventory,
            survivors,
          )
          if (cookingActivation) quests = cookingActivation

          if (recruitingNow && found) {
            const uRec = incrementUtrechtGatewayRecruit(
              quests,
              found.role,
              prev.workstations,
            )
            if (uRec) quests = uRec
          }

          // Advance cooking quest phase 0→1 if book-home-cooking is in kept loot
          if (keptLoot.some((s) => s.defId === 'book-home-cooking')) {
            const bookUpdate = reduceCookingSkillQuest(quests, {
              type: 'book_obtained',
              bookDefId: 'book-home-cooking',
            })
            if (bookUpdate) quests = bookUpdate
          }

          return {
            inventory,
            survivors,
            garageVehicles,
            quests,
            stats,
            achievementsUnlocked: nextAchievements,
            expeditions: prev.expeditions.filter((entry) => entry.id !== expeditionId),
            pets,
          }
        })
      },

      runWorkstation: (workstationId, survivorId) => {
        const state = get()
        const def = WORKSTATIONS[workstationId]
        const ws = state.workstations[workstationId]
        if (!ws?.built) return

        if (workstationId === 'training_yard') {
          const tws = state.workstations.training_yard
          const tid = tws?.assignedSurvivorId
          if (!tws?.built || !tid || tid !== survivorId) return
          const trainer = state.survivors.find((s) => s.id === tid)
          if (!trainer || trainer.status !== 'Idle') return
          const now = Date.now()
          const ends = tws.trainingYardSessionEndsAt ?? null
          if (ends != null && ends > now) return
          queueSyncAction('runWorkstation')
          set({
            workstations: {
              ...state.workstations,
              training_yard: {
                ...tws,
                trainingYardSessionEndsAt: now + 95_000,
              },
            },
          })
          return
        }

        if (workstationId === 'market_stall') {
          const mws = state.workstations.market_stall
          const tid = mws?.assignedSurvivorId
          if (!mws?.built || !tid) return
          const trader = state.survivors.find((s) => s.id === tid)
          if (!trader || trader.role !== 'Trader') return
          const now = Date.now()
          const endsAt = mws.tradingMissionEndsAt ?? null
          const activeSid = mws.tradingMissionSurvivorId ?? null

          if (
            endsAt != null &&
            endsAt <= now &&
            activeSid &&
            trader.id === activeSid &&
            trader.status === 'OnMission'
          ) {
            const payout = 18 + Math.floor(Math.random() * 14)
            queueSyncAction('runWorkstation')
            set({
              trinketPoints: state.trinketPoints + payout,
              survivors: state.survivors.map((s) =>
                s.id === activeSid ? { ...s, status: 'Idle' as const } : s,
              ),
              workstations: {
                ...state.workstations,
                market_stall: {
                  ...mws,
                  tradingMissionEndsAt: null,
                  tradingMissionSurvivorId: null,
                },
              },
            })
            return
          }

          if (trader.status !== 'Idle') return
          if (endsAt != null && endsAt > now) return

          const missionEnds = now + 3 * 60 * 1000
          queueSyncAction('runWorkstation')
          set({
            survivors: state.survivors.map((s) =>
              s.id === tid ? { ...s, status: 'OnMission' as const } : s,
            ),
            workstations: {
              ...state.workstations,
              market_stall: {
                ...mws,
                tradingMissionEndsAt: missionEnds,
                tradingMissionSurvivorId: tid,
              },
            },
          })
          return
        }

        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor) return

        if (survivor.status === 'OnMission') return

        if (def?.kind === 'injuryCare') {
          if (!def.category) return
          if (survivor.status !== 'Injured') return
          const { inventory: newInventory, consumed } = consumeOne(
            state.inventory,
            def.category,
          )
          if (!consumed) return
          let energyBonus = 0
          if (consumed.id === 'meds-field-kit') energyBonus = 18
          if (consumed.id === 'meds-antibiotics') energyBonus = 28
          queueSyncAction('runWorkstation')
          set({
            inventory: newInventory,
            survivors: state.survivors.map((s) =>
              s.id === survivorId
                ? {
                    ...s,
                    status: 'Idle' as const,
                    injuryEndsAt: null,
                    needs: {
                      ...s.needs,
                      energy: clamp(s.needs.energy + energyBonus),
                    },
                  }
                : s,
            ),
          })
          return
        }

        if (!def || def.kind !== 'restoreNeed') return
        if (!def.category || !def.need || !def.needRestore) return

        const { inventory: newInventory, consumed } = consumeOne(
          state.inventory,
          def.category,
        )
        if (!consumed) return

        const need = def.need as keyof Needs
        const assignedSurvivor = ws.assignedSurvivorId
          ? state.survivors.find((s) => s.id === ws.assignedSurvivorId)
          : undefined

        const restoreAmt = computeNeedRestoreAmount(
          workstationId,
          def.needRestore,
          ws,
          assignedSurvivor,
        )

        const updatedSurvivor: Survivor = {
          ...survivor,
          needs: {
            ...survivor.needs,
            [need]: clamp(survivor.needs[need] + restoreAmt),
          },
        }

        let questsNext = state.quests
        if (workstationId === 'kitchen') {
          const qr = reduceStarterQuest(state.quests, { type: 'kitchen_feed' })
          if (qr) {
            questsNext = qr
            queueSyncAction('questsUpdated')
          }
        }

        queueSyncAction('runWorkstation')
        set({
          inventory: newInventory,
          survivors: state.survivors.map((s) =>
            s.id === survivorId ? updatedSurvivor : s,
          ),
          quests: questsNext,
        })
      },

      installComputerLabPart: (slotIndex, defId) => {
        if (slotIndex < 0 || slotIndex > 3) return
        const state = get()
        const lab = state.workstations.computer_lab
        if (!lab?.built) return
        const slots = [...(lab.computerLabSlots ?? [null, null, null, null])] as [
          string | null,
          string | null,
          string | null,
          string | null,
        ]
        const ends = [...(lab.computerLabJobEndsAt ?? [null, null, null, null])] as [
          number | null,
          number | null,
          number | null,
          number | null,
        ]
        if (ends[slotIndex] != null && ends[slotIndex]! > Date.now()) return

        let inventory = state.inventory
        const prevPart = slots[slotIndex]
        if (prevPart) {
          inventory = addItems(inventory, [{ defId: prevPart, quantity: 1 }])
        }
        if (defId === null) {
          slots[slotIndex] = null
          ends[slotIndex] = null
          queueSyncAction('inventoryUpdated')
          set({
            inventory,
            workstations: {
              ...state.workstations,
              computer_lab: {
                ...lab,
                computerLabSlots: slots,
                computerLabJobEndsAt: ends,
              },
            },
          })
          return
        }
        if (!isComputerPartDefId(defId)) return
        const consumed = consumeByDefId(inventory, defId, 1)
        if (!consumed) return
        slots[slotIndex] = defId
        ends[slotIndex] = null
        queueSyncAction('inventoryUpdated')
        set({
          inventory: consumed,
          workstations: {
            ...state.workstations,
            computer_lab: {
              ...lab,
              computerLabSlots: slots,
              computerLabJobEndsAt: ends,
            },
          },
        })
      },

      assignComputerLabWorker: (slotIndex, survivorId) => {
        if (slotIndex < 0 || slotIndex > 3) return
        queueSyncAction('settingsUpdated')
        set((prev) => {
          const lab = prev.workstations.computer_lab
          if (!lab?.built) return {}
          const workers = [...(lab.computerLabWorkers ?? [null, null, null, null])] as [
            string | null,
            string | null,
            string | null,
            string | null,
          ]
          const ends = [...(lab.computerLabJobEndsAt ?? [null, null, null, null])] as [
            number | null,
            number | null,
            number | null,
            number | null,
          ]
          if (survivorId) {
            const surv = prev.survivors.find((s) => s.id === survivorId)
            if (!surv || surv.status === 'OnMission' || surv.status === 'Injured') return {}
            for (let i = 0; i < 4; i++) {
              if (i !== slotIndex && workers[i] === survivorId) {
                workers[i] = null
                ends[i] = null
              }
            }
            workers[slotIndex] = survivorId
          } else {
            workers[slotIndex] = null
            ends[slotIndex] = null
          }

          const nextWs = { ...prev.workstations }
          if (survivorId) {
            for (const id of Object.keys(nextWs) as WorkstationId[]) {
              if (id === 'computer_lab') continue
              const w = nextWs[id]
              if (w.assignedSurvivorId === survivorId) {
                nextWs[id] =
                  id === 'training_yard'
                    ? {
                        ...w,
                        assignedSurvivorId: null,
                        trainingYardSessionEndsAt: null,
                      }
                    : { ...w, assignedSurvivorId: null }
              }
            }
          }

          nextWs.computer_lab = {
            ...lab,
            computerLabWorkers: workers,
            computerLabJobEndsAt: ends,
          }
          return { workstations: nextWs }
        })
      },

      runComputerLabSlot: (slotIndex) => {
        if (slotIndex < 0 || slotIndex > 3) return
        const state = get()
        const lab = state.workstations.computer_lab
        if (!lab?.built) return
        const slots = lab.computerLabSlots ?? [null, null, null, null]
        const workers = lab.computerLabWorkers ?? [null, null, null, null]
        const ends = lab.computerLabJobEndsAt ?? [null, null, null, null]
        const part = slots[slotIndex]
        const worker = workers[slotIndex]
        const end = ends[slotIndex]
        const now = Date.now()
        if (!part || !worker) return
        const surv = state.survivors.find((s) => s.id === worker)
        if (!surv || surv.status !== 'Idle') return
        if (end != null && end > now) return
        const duration = computerLabJobDurationMs(part)
        queueSyncAction('runWorkstation')
        const nextEnds = [...ends] as [
          number | null,
          number | null,
          number | null,
          number | null,
        ]
        nextEnds[slotIndex] = now + duration
        set({
          workstations: {
            ...state.workstations,
            computer_lab: {
              ...lab,
              computerLabJobEndsAt: nextEnds,
            },
          },
        })
      },

      gemHealWorkstationNeed: (workstationId) => {
        if (!isGemHealableWorkstation(workstationId)) return
        const state = get()
        const ws = state.workstations[workstationId]
        if (!ws?.built) return
        if (state.gems < WORKSTATION_GEM_HEAL_COST) return
        const now = Date.now()
        if (
          typeof ws.gemHealAvailableAt === 'number' &&
          ws.gemHealAvailableAt > now
        ) {
          return
        }

        const survivors =
          workstationId === 'infirmary'
            ? state.survivors.map((survivor) =>
                survivor.status === 'Injured'
                  ? { ...survivor, status: 'Idle' as const, injuryEndsAt: null }
                  : survivor,
              )
            : (() => {
                const need = getRestoreNeedForWorkstation(workstationId)
                if (!need) return state.survivors
                return state.survivors.map((survivor) => {
                  if (survivor.status === 'OnMission') return survivor
                  const healedValue = 100
                  if (healedValue === survivor.needs[need]) return survivor
                  return {
                    ...survivor,
                    needs: {
                      ...survivor.needs,
                      [need]: healedValue,
                    },
                  }
                })
              })()

        queueSyncAction('settingsUpdated')
        set({
          gems: state.gems - WORKSTATION_GEM_HEAL_COST,
          survivors,
          workstations: {
            ...state.workstations,
            [workstationId]: {
              ...ws,
              gemHealAvailableAt: now + WORKSTATION_GEM_HEAL_COOLDOWN_MS,
            },
          },
        })
      },

      claimWorkstationGeneratedItem: (stationId, defId) => {
        if (!isGeneratorWorkstationId(stationId)) return
        const state = get()
        const ws = state.workstations[stationId]
        if (!ws || ws.generatedItems.length === 0) return
        const { nextGeneratedItems, claimed } = pullOneGeneratedItem(ws.generatedItems, defId)
        if (claimed.length === 0) return
        queueSyncAction('inventoryUpdated')
        set({
          inventory: addItems(state.inventory, claimed),
          workstations: {
            ...state.workstations,
            [stationId]: {
              ...ws,
              generatedItems: nextGeneratedItems,
            },
          },
        })
      },

      claimAllWorkstationGeneratedItems: (stationId) => {
        if (!isGeneratorWorkstationId(stationId)) return
        const state = get()
        const ws = state.workstations[stationId]
        if (!ws || ws.generatedItems.length === 0) return
        queueSyncAction('inventoryUpdated')
        set({
          inventory: addItems(state.inventory, ws.generatedItems),
          workstations: {
            ...state.workstations,
            [stationId]: {
              ...ws,
              generatedItems: [],
            },
          },
        })
      },

      useItemFromStash: (defId, survivorId) => {
        const state = get()
        const itemDef = getItemDef(defId)
        if (!itemDef) return
        if (
          itemDef.category !== 'food' &&
          itemDef.category !== 'water' &&
          itemDef.category !== 'meds'
        ) {
          return
        }

        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor || survivor.status === 'OnMission') return

        if (itemDef.category === 'meds') {
          if (survivor.status !== 'Injured') return
          const nextInventory = consumeByDefId(state.inventory, defId, 1)
          if (!nextInventory) return
          let energyBonus = 0
          if (defId === 'meds-field-kit') energyBonus = 18
          if (defId === 'meds-antibiotics') energyBonus = 28
          queueSyncAction('inventoryUpdated')
          set({
            inventory: nextInventory,
            survivors: state.survivors.map((s) =>
              s.id === survivorId
                ? {
                    ...s,
                    status: 'Idle' as const,
                    injuryEndsAt: null,
                    needs: {
                      ...s.needs,
                      energy: clamp(s.needs.energy + energyBonus),
                    },
                  }
                : s,
            ),
          })
          return
        }

        const nextInventory = consumeByDefId(state.inventory, defId, 1)
        if (!nextInventory) return

        const sourceWorkstationId: WorkstationId =
          itemDef.category === 'food' ? 'kitchen' : 'well'
        const sourceDef = WORKSTATIONS[sourceWorkstationId]

        if (
          sourceDef.kind !== 'restoreNeed' ||
          !sourceDef.need ||
          !sourceDef.needRestore
        ) {
          return
        }

        const restoreAmount = computeStashNeedRestorePreview(
          itemDef.category,
          state.workstations,
          state.survivors,
        )

        const need = sourceDef.need as keyof Needs
        let bonusEnergy = 0
        if (defId === 'water-mineral-sealed') bonusEnergy = 10

        const updatedSurvivor: Survivor = {
          ...survivor,
          needs: {
            ...survivor.needs,
            [need]: clamp(survivor.needs[need] + restoreAmount),
            energy: clamp(survivor.needs.energy + bonusEnergy),
          },
        }

        queueSyncAction('inventoryUpdated')
        set({
          inventory: nextInventory,
          survivors: state.survivors.map((s) =>
            s.id === survivorId ? updatedSurvivor : s,
          ),
        })
      },

      resolveEvent: (survivorId, choiceIndex) => {
        const state = get()
        const pending = state.pendingEvents.find(
          (p) => p.survivorId === survivorId,
        )
        if (!pending) return
        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor) return

        const choice = pending.event.choices[choiceIndex]

        // Apply base loot
        let inventory = addItems(state.inventory, pending.baseLoot)
        // Apply event grants
        if (choice.effect.grantItems) {
          inventory = addItems(inventory, choice.effect.grantItems)
        }
        // Apply event takes (per-category, lowest-rarity first)
        const removed: ItemStack[] = []
        if (choice.effect.takeCategory) {
          for (const take of choice.effect.takeCategory) {
            for (let i = 0; i < take.amount; i++) {
              const result = consumeOne(inventory, take.category)
              if (!result.consumed) break
              inventory = result.inventory
              const last = removed[removed.length - 1]
              if (last && last.defId === result.consumed.id) {
                removed[removed.length - 1] = {
                  ...last,
                  quantity: last.quantity + 1,
                }
              } else {
                removed.push({ defId: result.consumed.id, quantity: 1 })
              }
            }
          }
        }

        // Apply needs delta
        let updatedSurvivor = survivor
        if (choice.effect.needsDelta) {
          const d = choice.effect.needsDelta
          const legacy = d as Partial<Needs> & { morale?: number }
          updatedSurvivor = {
            ...survivor,
            needs: {
              hunger: clamp(survivor.needs.hunger + (d.hunger ?? 0)),
              thirst: clamp(survivor.needs.thirst + (d.thirst ?? 0)),
              energy: clamp(
                survivor.needs.energy + (d.energy ?? legacy.morale ?? 0),
              ),
            },
          }
        }

        const delivered = addItems(pending.baseLoot, choice.effect.grantItems ?? [])

        const toast: Toast = {
          id: `toast-event-${Date.now()}`,
          survivorName: survivor.name,
          delivered,
          removed: removed.length > 0 ? removed : undefined,
        }

        queueSyncAction('resolveEvent')
        set((prev) => ({
          inventory,
          survivors: state.survivors.map((s) =>
            s.id === survivorId ? updatedSurvivor : s,
          ),
          pendingEvents: prev.pendingEvents.filter(
            (p) => p.survivorId !== survivorId,
          ),
          toasts: [...prev.toasts, toast],
        }))
      },

      dismissToast: (id) => {
        set((prev) => ({
          toasts: prev.toasts.filter((t) => t.id !== id),
        }))
      },

      setInventoryLock: (defId, locked) => {
        queueSyncAction('inventoryUpdated')
        set((state) => ({
          inventory: state.inventory.map((stack) =>
            stack.defId === defId ? { ...stack, locked } : stack,
          ),
        }))
      },

      bulkDiscard: (defIds) => {
        if (defIds.length === 0) return
        const unique = new Set(defIds)
        queueSyncAction('inventoryUpdated')
        set((state) => {
          let converted = 0
          const nextInventory = state.inventory.filter((stack) => {
            if (!unique.has(stack.defId)) return true
            const def = getItemDef(stack.defId)
            if (def) converted += def.value * stack.quantity
            return false
          })
          return {
            inventory: nextInventory,
            trinketPoints: state.trinketPoints + converted,
          }
        })
      },

      startCraft: (recipeId) => {
        const state = get()
        const recipe = RECIPES[recipeId]
        if (!recipe) return
        if (state.progress < recipe.unlockAt) return

        const wsCraft = state.workstations[recipe.workstationId]
        if (!wsCraft?.built) return
        const minLv = recipe.minWorkstationLevel ?? 1
        if (wsCraft.level < minLv) return

        const hasBusyStation = state.craftJobs.some(
          (job) => job.workstationId === recipe.workstationId,
        )
        if (hasBusyStation) return
        const consumed = consumeForRecipe(state.inventory, recipe.inputs)
        if (!consumed) return
        queueSyncAction('craftRecipe')
        const now = Date.now()
        const durationMs = computeCraftDurationMs(
          recipe.workstationId,
          recipe.durationMs,
          wsCraft,
        )
        if (durationMs <= 0 && recipe.output.type === 'item') {
          trackEvent('recipe_crafted', { recipe_id: recipeId })
          playRecipeCraftCue(state.settings.soundEnabled)
          const assigneeId = wsCraft.assignedSurvivorId
          const xpGrant = recipe.skillXp
          let survivorsNext = state.survivors
          if (xpGrant && assigneeId) {
            survivorsNext = state.survivors.map((s) =>
              s.id === assigneeId
                ? {
                    ...s,
                    skills: grantSkillXp(s.skills, xpGrant.skill, xpGrant.amount),
                  }
                : s,
            )
          }
          set({
            inventory: addItems(consumed, [
              { defId: recipe.output.defId, quantity: recipe.output.quantity },
            ]),
            survivors: survivorsNext,
            toasts: [
              ...state.toasts,
              {
                id: `toast-craft-now-${now}`,
                survivorName: recipe.workstationId,
                delivered: [
                  { defId: recipe.output.defId, quantity: recipe.output.quantity },
                ],
              },
            ],
          })
          return
        }
        set({
          inventory: consumed,
          craftJobs: [
            ...state.craftJobs,
            {
              id: nextCraftJobId(now),
              recipeId: recipe.id,
              workstationId: recipe.workstationId,
              startedAt: now,
              endsAt: now + durationMs,
              assignedSurvivorId: wsCraft.assignedSurvivorId ?? null,
            },
          ],
        })
      },

      buildWorkstation: (stationId) => {
        const state = get()
        const ws = state.workstations[stationId]
        if (!ws || ws.built || ws.level !== 0) return
        const gate = getInitialBuildGate(stationId)
        if (!gate) return
        if (!meetsHideoutTierMinimum(state.hideoutTierId, gate.minHideoutTier)) return
        const consumed = consumeForRecipe(state.inventory, gate.costs)
        if (!consumed) return
        let questsNext = state.quests
        const event: QuestProgressEvent = { type: 'workstation_built', stationId }
        const qr = reduceStarterQuest(state.quests, event)
        if (qr) {
          questsNext = qr
          queueSyncAction('questsUpdated')
        }
        const qv = reduceVehicleQuest(questsNext, event)
        if (qv) {
          questsNext = qv
          queueSyncAction('questsUpdated')
        }
        queueSyncAction('inventoryUpdated')
        const workstationsNext: Record<WorkstationId, WorkstationRuntimeState> = {
          ...state.workstations,
          [stationId]: { ...ws, built: true, level: 1 },
        }
        let questsFinal = questsNext
        const qUtrechtDone = tryCompleteUtrechtGateway(questsNext, workstationsNext)
        if (qUtrechtDone) {
          questsFinal = qUtrechtDone
          queueSyncAction('questsUpdated')
        }
        set({
          inventory: consumed,
          workstations: workstationsNext,
          quests: questsFinal,
        })
      },

      repairVehicle: (vehicleInstanceId) => {
        const state = get()
        const vehicle = state.garageVehicles.find((v) => v.id === vehicleInstanceId)
        if (!vehicle || vehicle.durability >= 100) return
        const nextInventory = consumeByDefId(
          state.inventory,
          'scrap-rusty',
          VEHICLE_REPAIR_SCRAP_COST,
        )
        if (!nextInventory) return
        const repaired = Math.min(
          100,
          vehicle.durability + VEHICLE_REPAIR_DURABILITY_PER_SCRAP,
        )
        queueSyncAction('inventoryUpdated')
        set({
          inventory: nextInventory,
          garageVehicles: state.garageVehicles.map((v) =>
            v.id === vehicleInstanceId ? { ...v, durability: repaired } : v,
          ),
        })
      },

      upgradeVehiclePart: (vehicleInstanceId, partId) => {
        const state = get()
        const vehicle = state.garageVehicles.find((v) => v.id === vehicleInstanceId)
        if (!vehicle || vehicle.durability <= 0) return
        if (!state.workstations.garage?.built) return
        if (vehicleIsOnActiveExpedition(state.expeditions, vehicleInstanceId)) return

        const def = getVehicleDef(vehicle.defId)
        if (!def) return

        const part = def.parts.find((p) => p.id === partId)
        if (!part) return

        const currentLevel = (vehicle.partLevels?.[partId] ?? 1) as 1 | 2 | 3
        if (currentLevel >= 3) return

        const nextLevel: 2 | 3 = currentLevel === 1 ? 2 : 3
        const upgrade = part.upgrades[nextLevel]
        if (!upgrade) return

        const nextInventory = consumeForRecipe(state.inventory, upgrade.costs)
        if (!nextInventory) return

        queueSyncAction('inventoryUpdated')
        set({
          inventory: nextInventory,
          garageVehicles: state.garageVehicles.map((v) =>
            v.id !== vehicleInstanceId
              ? v
              : {
                  ...v,
                  partLevels: { ...v.partLevels, [partId]: nextLevel },
                },
          ),
        })
      },

      scrapVehicle: (vehicleInstanceId) => {
        const state = get()
        if (!state.workstations.garage?.built) return
        if (vehicleIsOnActiveExpedition(state.expeditions, vehicleInstanceId)) return
        const vehicle = state.garageVehicles.find((v) => v.id === vehicleInstanceId)
        if (!vehicle) return
        const stacks = scrapYieldForVehicleDef(vehicle.defId)
        const now = Date.now()
        queueSyncAction('inventoryUpdated')
        trackEvent('vehicle_scrapped', { def_id: vehicle.defId })
        set({
          inventory: addItems(state.inventory, stacks),
          garageVehicles: state.garageVehicles.filter((v) => v.id !== vehicleInstanceId),
          toasts: [
            ...state.toasts,
            {
              id: `toast-scrap-veh-${vehicleInstanceId}-${now}`,
              survivorName: 'shelter',
              message: lt(
                'Voertuig gesloopt — onderdelen naar de voorraad.',
                'Vehicle scrapped — parts moved to stash.',
              ),
              delivered: [],
            },
          ],
        })
      },

      acknowledgeVehicleQuestCompletion: () => {
        const state = get()
        const vq = state.quests.vehicleQuest
        if (vq.status !== 'completed' || vq.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          quests: {
            ...state.quests,
            vehicleQuest: { ...vq, acknowledged: true },
          },
        })
      },

      recheckVehicleQuestGarage: () => {
        const state = get()
        const vq = state.quests.vehicleQuest
        if (vq.status !== 'active' || vq.phaseIndex !== 0) return
        if (!state.workstations.garage?.built) return
        const event: QuestProgressEvent = { type: 'workstation_built', stationId: 'garage' }
        const next = reduceVehicleQuest(state.quests, event)
        if (!next) return
        queueSyncAction('questsUpdated')
        set({ quests: next })
      },

      upgradeWorkstation: (stationId) => {
        const state = get()
        const ws = state.workstations[stationId]
        if (!ws?.built || ws.level < 1) return
        const gate = getUpgradeGateForNextLevel(stationId, ws.level)
        if (!gate) return
        if (!meetsHideoutTierMinimum(state.hideoutTierId, gate.minHideoutTier)) return
        if (!canReachStationLevel(state.hideoutTierId, stationId, ws.level + 1)) return
        const consumed = consumeForRecipe(state.inventory, gate.costs)
        if (!consumed) return

        const newLevel = ws.level + 1
        let quests = state.quests
        const questUpdate = reduceHideoutMoveQuest(quests, {
          type: 'workstation_upgraded',
          stationId,
          newLevel,
        })
        if (questUpdate) {
          quests = questUpdate
          queueSyncAction('questsUpdated')
        }

        queueSyncAction('inventoryUpdated')
        set({
          inventory: consumed,
          workstations: {
            ...state.workstations,
            [stationId]: { ...ws, level: newLevel },
          },
          quests,
        })
      },

      relocateHideout: () => {
        const state = get()
        const def = RELOCATION_FROM_TIER[state.hideoutTierId]
        if (!def) return
        if (state.progress < def.minProgress) return
        const consumed = consumeForRecipe(state.inventory, def.costs)
        if (!consumed) return

        let quests = state.quests
        const questUpdate = reduceHideoutMoveQuest(quests, { type: 'hideout_relocated' })
        if (questUpdate) {
          quests = questUpdate
          queueSyncAction('questsUpdated')
        }

        const stats = { ...state.stats, hideoutMoves: state.stats.hideoutMoves + 1 }
        const nextAchievements = resolveUnlockedAchievements(stats, state.achievementsUnlocked)

        queueSyncAction('inventoryUpdated')
        set({
          inventory: consumed,
          hideoutTierId: def.to,
          quests,
          stats,
          achievementsUnlocked: nextAchievements,
        })
      },

      assignSurvivorToWorkstation: (stationId, survivorId) => {
        const state = get()
        if (stationId === 'kitchen' && survivorId) {
          const surv = state.survivors.find((s) => s.id === survivorId)
          if (!surv || !isSkillUnlocked(surv, 'cooking')) return
        }

        // Fire kitchen_assigned quest event when a cook is assigned
        let quests = state.quests
        if (stationId === 'kitchen' && survivorId) {
          const questUpdate = reduceCookingSkillQuest(quests, { type: 'kitchen_assigned' })
          if (questUpdate) {
            quests = questUpdate
            queueSyncAction('questsUpdated')
          }
        }

        queueSyncAction('settingsUpdated')
        set((prev) => {
          const next = { ...prev.workstations }
          if (survivorId) {
            for (const id of Object.keys(next) as WorkstationId[]) {
              if (id === stationId) continue
              let w = next[id]
              if (w.assignedSurvivorId === survivorId) {
                w =
                  id === 'training_yard'
                    ? {
                        ...w,
                        assignedSurvivorId: null,
                        trainingYardSessionEndsAt: null,
                      }
                    : { ...w, assignedSurvivorId: null }
              }
              if (id === 'computer_lab') {
                const cw = w.computerLabWorkers
                if (cw) {
                  const workers = [...cw] as (string | null)[]
                  const ce =
                    w.computerLabJobEndsAt ?? [null, null, null, null]
                  const jobEnds = [...ce] as (number | null)[]
                  for (let i = 0; i < 4; i++) {
                    if (workers[i] === survivorId) {
                      workers[i] = null
                      jobEnds[i] = null
                    }
                  }
                  w = {
                    ...w,
                    computerLabWorkers: workers as WorkstationRuntimeState['computerLabWorkers'],
                    computerLabJobEndsAt:
                      jobEnds as WorkstationRuntimeState['computerLabJobEndsAt'],
                  }
                }
              }
              next[id] = w
            }
          }
          next[stationId] = {
            ...next[stationId],
            assignedSurvivorId: survivorId,
          }
          return { workstations: next, quests }
        })
      },

      addLodgingOccupant: (survivorId) => {
        const state = get()
        const lodgingWs = state.workstations.lodging
        if (!lodgingWs?.built || lodgingWs.level < 1) return
        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor || survivor.status !== 'Idle') return
        const cap = getLodgingRestCapacity(lodgingWs)
        const occ = lodgingWs.lodgingOccupantIds ?? []
        if (occ.includes(survivorId) || occ.length >= cap) return
        queueSyncAction('settingsUpdated')
        set({
          workstations: {
            ...state.workstations,
            lodging: {
              ...lodgingWs,
              lodgingOccupantIds: [...occ, survivorId],
            },
          },
        })
      },

      removeLodgingOccupant: (survivorId) => {
        const state = get()
        const lodgingWs = state.workstations.lodging
        if (!lodgingWs?.built) return
        const occ = lodgingWs.lodgingOccupantIds ?? []
        if (!occ.includes(survivorId)) return
        queueSyncAction('settingsUpdated')
        set({
          workstations: {
            ...state.workstations,
            lodging: {
              ...lodgingWs,
              lodgingOccupantIds: occ.filter((id) => id !== survivorId),
            },
          },
        })
      },

      acceptBroadcastRecruit: (offerSurvivorId) => {
        const state = get()
        const ws = state.workstations.broadcast_station
        if (!ws?.built) return
        const idx = ws.broadcastRecruitOffers.findIndex((s) => s.id === offerSurvivorId)
        if (idx < 0) return
        const offer = ws.broadcastRecruitOffers[idx]!
        const cap = getMaxSurvivors(state.workstations)
        if (state.survivors.length >= cap) return
        if (state.survivors.some((s) => s.id === offer.id)) return
        const nextOffers = ws.broadcastRecruitOffers.filter((s) => s.id !== offerSurvivorId)
        let stats = state.stats
        let achievementsUnlocked = state.achievementsUnlocked
        const survivors = [...state.survivors, { ...offer, status: 'Idle' as const }]
        stats = { ...stats, survivorsRecruited: survivors.length }
        achievementsUnlocked = resolveUnlockedAchievements(stats, achievementsUnlocked)
        let quests = state.quests
        const cookingActivation = reduceCookingSkillQuestAvailability(
          quests,
          survivors.length,
          state.inventory,
          survivors,
        )
        if (cookingActivation) quests = cookingActivation

        const utrechtRec = incrementUtrechtGatewayRecruit(
          quests,
          offer.role,
          state.workstations,
        )
        if (utrechtRec) quests = utrechtRec

        queueSyncAction('settingsUpdated')
        queueSyncAction('questsUpdated')
        set({
          survivors,
          stats,
          achievementsUnlocked,
          quests,
          workstations: {
            ...state.workstations,
            broadcast_station: {
              ...ws,
              broadcastRecruitOffers: nextOffers,
            },
          },
        })
      },

      denyBroadcastRecruit: (offerSurvivorId) => {
        const state = get()
        const ws = state.workstations.broadcast_station
        if (!ws?.built) return
        if (!ws.broadcastRecruitOffers.some((s) => s.id === offerSurvivorId)) return
        queueSyncAction('settingsUpdated')
        set({
          workstations: {
            ...state.workstations,
            broadcast_station: {
              ...ws,
              broadcastRecruitOffers: ws.broadcastRecruitOffers.filter(
                (s) => s.id !== offerSurvivorId,
              ),
            },
          },
        })
      },

      installWorkstationTool: (stationId, toolId) => {
        const tool = WORKSTATION_TOOLS[toolId]
        if (!tool || tool.workstationId !== stationId) return
        const state = get()
        const ws = state.workstations[stationId]
        if (!ws || !ws.built || ws.level < tool.requiredStationLevel) return
        if (ws.installedToolIds.includes(toolId)) return
        const consumed = consumeForRecipe(state.inventory, tool.costs)
        if (!consumed) return
        queueSyncAction('inventoryUpdated')
        set({
          inventory: consumed,
          workstations: {
            ...state.workstations,
            [stationId]: {
              ...ws,
              installedToolIds: [...ws.installedToolIds, toolId],
            },
          },
        })
      },

      equipWeaponFromStash: (survivorId, weaponDefId) => {
        const state = get()
        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor) return
        const def = getItemDef(weaponDefId)
        if (!def || def.category !== 'weapon' || !def.weapon) return
        if (def.weapon.family === 'bow' && !isSkillUnlocked(survivor, 'archery')) return
        const consumed = consumeByDefId(state.inventory, weaponDefId, 1)
        if (!consumed) return
        let inventory = consumed
        const prevWeapon = survivor.loadout.weapon
        if (prevWeapon?.weaponDefId) {
          inventory = addItems(inventory, [{ defId: prevWeapon.weaponDefId, quantity: 1 }])
        }
        const equipped = equipmentFromWeaponDef(weaponDefId)
        if (!equipped) return
        queueSyncAction('inventoryUpdated')
        set({
          inventory,
          survivors: state.survivors.map((s) =>
            s.id === survivorId
              ? { ...s, loadout: { ...s.loadout, weapon: equipped } }
              : s,
          ),
        })
      },

      unequipWeaponToStash: (survivorId) => {
        const state = get()
        const survivor = state.survivors.find((s) => s.id === survivorId)
        if (!survivor) return
        const w = survivor.loadout.weapon
        if (!w?.weaponDefId) return
        queueSyncAction('inventoryUpdated')
        set({
          inventory: addItems(state.inventory, [{ defId: w.weaponDefId, quantity: 1 }]),
          survivors: state.survivors.map((s) =>
            s.id === survivorId ? { ...s, loadout: { ...s.loadout, weapon: null } } : s,
          ),
        })
      },

      learnSkillFromBook: (survivorId, bookDefId) => {
        const state = get()
        const skillId = BOOK_SKILL_MAP[bookDefId]
        if (!skillId) return
        const survivor = state.survivors.find((entry) => entry.id === survivorId)
        if (!survivor) return
        const consumed = state.inventory.find((stack) => stack.defId === bookDefId)
        if (!consumed || consumed.quantity < 1) return
        const nextInventory = state.inventory
          .map((stack) =>
            stack.defId === bookDefId ? { ...stack, quantity: stack.quantity - 1 } : stack,
          )
          .filter((stack) => stack.quantity > 0)
        const nextSkills = unlockSkill(survivor.skills, skillId)

        // Update cooking quest on skill_learned event
        let quests = state.quests
        const questUpdate = reduceCookingSkillQuest(quests, {
          type: 'skill_learned',
          skillId,
        })
        if (questUpdate) {
          quests = questUpdate
          queueSyncAction('questsUpdated')
        }

        queueSyncAction('inventoryUpdated')
        set({
          inventory: nextInventory,
          survivors: state.survivors.map((entry) =>
            entry.id === survivorId ? { ...entry, skills: nextSkills } : entry,
          ),
          quests,
          toasts: [
            ...state.toasts,
            {
              id: `toast-book-${survivorId}-${bookDefId}-${Date.now()}`,
              survivorName: survivor.name,
              message: {
                nl: `${survivor.name} heeft ${skillId} geleerd.`,
                en: `${survivor.name} learned ${skillId}.`,
              },
              delivered: [],
            },
          ],
        })
      },

      claimDailyLoginBonus: () => {
        const state = get()
        const today = new Date().toISOString().slice(0, 10)
        if (state.dailyLastClaimedAt === today) return
        const pool = [
          'food-stamppot',
          'water-mineral-sealed',
          'meds-bandages',
          'scrap-electronics',
        ]
        const rewardDefId = pool[Math.floor(Math.random() * pool.length)]
        queueSyncAction('inventoryUpdated')
        set({
          dailyLastClaimedAt: today,
          inventory: addItems(state.inventory, [{ defId: rewardDefId, quantity: 1 }]),
          toasts: [
            ...state.toasts,
            {
              id: `toast-daily-${today}`,
              survivorName: 'shelter',
              message: {
                nl: 'Dagelijkse bonus ontvangen.',
                en: 'Daily bonus claimed.',
              },
              delivered: [{ defId: rewardDefId, quantity: 1 }],
            },
          ],
        })
      },

      advanceOnboarding: () => {
        queueSyncAction('onboardingUpdated')
        const state = get()
        const next =
          state.onboardingStep === 'done'
            ? 'done'
            : state.onboardingStep === 'not_started'
              ? 'character_create'
              : nextOnboardingStep(state.onboardingStep)
        let quests = state.quests
        let tutorialStep: TutorialStep | null = state.tutorialStep
        if (state.onboardingStep === 'intro' && next === 'done') {
          quests = mergeQuestRuntimeState({
            ...state.quests,
            starterSurvival: initialStarterQuestActive(),
          })
          queueSyncAction('questsUpdated')
          tutorialStep = 'hideout_resources'
        }
        set({ onboardingStep: next, tutorialStep, quests })
      },

      advanceTutorial: () => {
        const state = get()
        if (!state.tutorialStep || state.tutorialStep === 'done') return
        const order: TutorialStep[] = [
          'hideout_resources',
          'hideout_workstations',
          'nav_tabs',
          'survivors_list',
          'stash_items',
          'expeditions_locations',
          'quests_quest',
          'quests_milestones',
          'done',
        ]
        const idx = order.indexOf(state.tutorialStep)
        const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : 'done'
        set({ tutorialStep: next })
      },

      claimAchievement: (id) => {
        const state = get()
        if (!state.achievementsUnlocked.includes(id)) return
        if (state.achievementsClaimed.includes(id)) return
        const reward = gemRewardForAchievement(id)
        set({
          achievementsClaimed: [...state.achievementsClaimed, id],
          gems: state.gems + reward,
        })
      },

      trackQuestProgress: (event) => {
        const state = get()
        const next =
          reduceStarterQuest(state.quests, event) ??
          reduceCookingSkillQuest(state.quests, event)
        if (!next) return
        queueSyncAction('questsUpdated')
        set({ quests: next })
      },

      acknowledgeStarterQuestCompletion: () => {
        const state = get()
        const sq = state.quests.starterSurvival
        if (sq.status !== 'completed' || sq.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          gems: state.gems + 20,
          quests: {
            ...state.quests,
            starterSurvival: { ...sq, acknowledged: true },
          },
        })
      },

      acknowledgeDenBoschGatewayCompletion: () => {
        const state = get()
        const g = state.quests.denBoschGateway
        if (g.status !== 'completed' || g.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          quests: {
            ...state.quests,
            denBoschGateway: { ...g, acknowledged: true },
          },
        })
      },

      acknowledgeCookingQuestCompletion: () => {
        const state = get()
        const cq = state.quests.cookingSkillQuest
        if (cq.status !== 'completed' || cq.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          gems: state.gems + 20,
          quests: {
            ...state.quests,
            cookingSkillQuest: { ...cq, acknowledged: true },
          },
        })
      },

      acknowledgeHideoutMoveQuestCompletion: () => {
        const state = get()
        const hq = state.quests.hideoutMoveQuest
        if (hq.status !== 'completed' || hq.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          gems: state.gems + 20,
          quests: {
            ...state.quests,
            hideoutMoveQuest: { ...hq, acknowledged: true },
          },
        })
      },

      acknowledgeUtrechtGatewayCompletion: () => {
        const state = get()
        const uq = state.quests.utrechtGateway
        if (uq.status !== 'completed' || uq.acknowledged) return
        queueSyncAction('questsUpdated')
        set({
          gems: state.gems + 25,
          quests: {
            ...state.quests,
            utrechtGateway: { ...uq, acknowledged: true },
          },
        })
      },

      startQuest: (questId) => {
        queueSyncAction('questsUpdated')
        set((prev) => {
          if (questId === DEN_BOSCH_GATEWAY_QUEST_ID) {
            const next = reduceDenBoschGatewayStart(prev.quests)
            if (!next) return prev
            return { quests: next }
          }
          if (questId === UTRECHT_GATEWAY_QUEST_ID) {
            const next = reduceUtrechtGatewayStart(prev.quests, prev.survivors.length)
            if (!next) return prev
            const synced =
              syncUtrechtGatewayPhaseFromState(
                next,
                prev.inventory,
                prev.workstations,
              ) ?? next
            return { quests: synced }
          }
          return prev
        })
      },

      skipExpeditionHalfTime: (expeditionId) => {
        const state = get()
        if (state.gems < 1) return
        const expedition = state.expeditions.find(
          (e) => e.id === expeditionId && e.status === 'active',
        )
        if (!expedition) return
        const now = Date.now()
        const halfTime = Math.floor((expedition.endsAt - expedition.startedAt) / 2)
        const newEndsAt = Math.max(now, expedition.endsAt - halfTime)
        set({
          gems: state.gems - 1,
          expeditions: state.expeditions.map((e) =>
            e.id === expeditionId ? { ...e, endsAt: newEndsAt } : e,
          ),
        })
        if (newEndsAt <= now) {
          get().tick(now)
        }
      },

      commitLeadSurvivor: (survivor) => {
        queueSyncAction('onboardingUpdated')
        set({ survivors: [survivor] })
      },

      resetProgressToNewGame: () => {
        const s = get().settings
        set({
          ...newGameDefaults(),
          settings: {
            ...DEFAULT_GAME_SETTINGS,
            language: s.language,
            notificationsEnabled: s.notificationsEnabled,
            soundEnabled: s.soundEnabled,
            theme: s.theme,
          },
        })
        queueSyncAction('inventoryUpdated')
        queueSyncAction('onboardingUpdated')
        void get().flushSync()
      },

      updateSettings: (partial) => {
        queueSyncAction('settingsUpdated')
        set((prev) => ({
          settings: { ...prev.settings, ...partial },
        }))
      },

      syncFromCloud: async () => {
        const user = useAuthStore.getState().user
        if (!user) return
        const state = get()
        if (state.isSyncing) return

        // If there are unsynced local changes, push them before pulling.
        // Without this, the pull would overwrite them with stale cloud state.
        //
        // Two flavours of "unsynced" both need to be flushed:
        //  - Same-session queue (hasQueuedSyncActions) covers actions that
        //    happened since the last successful push in the current tab.
        //  - Persisted flag (hasPendingSync) covers the cold-start case where
        //    a previous session made progress, wrote it to localStorage, but
        //    never managed to push (e.g. user refreshed before the 5s autosave
        //    fired, or beforeunload's POST got cancelled). The in-memory queue
        //    is gone but the flag survives.
        //
        // We intentionally do NOT gate this on `hydratedFromCloud`: that flag
        // is in-memory only and false at boot, which previously made this
        // branch unreachable from a cold start. The empty-push guard inside
        // pushChanges still protects against a device with wiped localStorage
        // pushing default state over a real cloud save.
        if (hasQueuedSyncActions() || hasPendingSync()) {
          set({ isSyncing: true, syncError: null })
          let preFlushFailed = false
          try {
            await pushChanges(user.id, toSyncedState(get()), user.email ?? undefined)
          } catch (error) {
            preFlushFailed = true
            captureSyncError('sync_flush_before_pull_failed', {
              kind: error instanceof Error ? error.name : 'unknown',
            })
          }
          set({ isSyncing: false })
          // Critical: if the pre-flush failed we still have unsynced local
          // work. Pulling now would replace it with the stale cloud snapshot
          // and that local work would be unrecoverable. Surface the error and
          // bail so a subsequent retry can flush before any pull happens.
          if (preFlushFailed) {
            set({
              syncError: friendlySyncError(
                new Error('pre-flush failed'),
                get().settings.language,
              ),
            })
            return
          }
        }

        set({ isSyncing: true, syncError: null })
        try {
          const result = await pullState(user.id)
          if (result == null) {
            // Supabase not configured. Don't touch local state.
            set({ isSyncing: false })
            return
          }
          if (result.kind === 'no-profile') {
            // Genuinely new account: leave local state alone (the onboarding
            // flow will create a save through normal user actions and the
            // first push will seed the cloud). Mark hydrated so subsequent
            // flushSync calls are allowed to push.
            set({
              isSyncing: false,
              syncError: null,
              lastSyncAt: Date.now(),
              hydratedFromCloud: true,
            })
            trackEvent('sync_pull', { result: 'no_profile' })
            return
          }
          const remoteState = result.state
          set((prev) => ({
            survivors: remoteState.survivors.map((s) => ensureSurvivorShape(s)),
            inventory: remoteState.inventory,
            expeditions: mergeExpeditionsPreservingUnclaimedReturns(
              remoteState.expeditions,
              prev.expeditions,
            ),
            pendingEvents: remoteState.pendingEvents,
            progress: remoteState.progress,
            locationExploration: {
              ...createInitialLocationExplorationState(),
              ...remoteState.locationExploration,
            },
            xp: remoteState.xp,
            onboardingStep: remoteState.onboardingStep as OnboardingStep,
            settings: normalizeRemoteSettings(remoteState.settings),
            hideoutTierId: remoteState.hideoutTierId ?? 'forest_hideout',
            workstations: mergeWorkstationStates(
              remoteState.workstations,
              'newGame',
            ),
            quests: (() => {
              let q = mergeQuestRuntimeState(remoteState.quests)
              const explorationMerged = {
                ...createInitialLocationExplorationState(),
                ...remoteState.locationExploration,
              }
              const qAvail = reduceDenBoschGatewaySyncAvailability(
                q,
                remoteState.progress,
                explorationMerged,
              )
              if (qAvail) q = qAvail
              const cookingAvail = reduceCookingSkillQuestAvailability(
                q,
                (remoteState.survivors ?? []).length,
                remoteState.inventory ?? [],
                remoteState.survivors ?? [],
              )
              if (cookingAvail) q = cookingAvail
              const hideoutMoveAvail = reduceHideoutMoveQuestAvailability(
                q,
                remoteState.hideoutTierId ?? 'forest_hideout',
                (remoteState.workstations as Record<string, { level?: number }> | undefined)?.base_station?.level ?? 1,
              )
              if (hideoutMoveAvail) q = hideoutMoveAvail
              const utrechtAvail = reduceUtrechtGatewayAvailability(
                q,
                explorationMerged,
              )
              if (utrechtAvail) q = utrechtAvail
              return q
            })(),
            garageVehicles: (remoteState.garageVehicles ?? [])
              .map((v) =>
                ensureVehicleInstanceShape({
                  ...(v ?? {}),
                  id: (v as any)?.id,
                  defId: (v as any)?.defId,
                } as VehicleInstance),
              )
              .filter((v) => typeof v.id === 'string' && typeof v.defId === 'string'),
            gems: remoteState.gems ?? prev.gems ?? 100,
            trinketPoints: remoteState.trinketPoints ?? prev.trinketPoints ?? 0,
            pets: remoteState.pets ?? prev.pets ?? [],
          }))

          set({
            isSyncing: false,
            syncError: null,
            lastSyncAt: Date.now(),
            hydratedFromCloud: true,
          })
          trackEvent('sync_pull', { result: 'hydrate' })
        } catch (error) {
          captureSyncError('sync_pull_failed', {
            kind: error instanceof Error ? error.name : 'unknown',
          })
          set({
            isSyncing: false,
            syncError: friendlySyncError(error, get().settings.language),
          })
        }
      },

      flushSync: async () => {
        const user = useAuthStore.getState().user
        if (!user) return
        const state = get()
        if (state.isSyncing) return
        if (!hasQueuedSyncActions()) return
        // Critical guard: never push before we've successfully hydrated from
        // the cloud at least once this session. Without this, a device that
        // lost its localStorage can push the default empty state and wipe
        // the cloud save.
        if (!state.hydratedFromCloud) return

        set({ isSyncing: true, syncError: null })
        try {
          await pushChanges(user.id, toSyncedState(get()), user.email ?? undefined)
          set({
            isSyncing: false,
            syncError: null,
            lastSyncAt: Date.now(),
          })
          trackEvent('sync_push', { result: 'ok' })
        } catch (error) {
          captureSyncError('sync_push_failed', {
            kind: error instanceof Error ? error.name : 'unknown',
          })
          set({
            isSyncing: false,
            syncError: friendlySyncError(error, get().settings.language),
          })
        }
      },
    }),
    {
      name: 'nederfall-shelter-v1',
      storage: createJSONStorage(() => localStorage),
      version: 22,
      migrate: (persisted, version) => {
        const p = (persisted ?? {}) as Partial<GameState> & {
          resources?: Partial<Record<string, number>>
        }
        if (version < 4) {
          const survivors = (p.survivors ?? []).map((s) =>
            ensureSurvivorShape(s as Partial<Survivor> & { id: string }),
          )
          const hasSurvivors = survivors.length > 0
          // Migrate resources to inventory; drop in-flight expeditions
          // (their location IDs may not exist in the new geography).
          const inventory = inventoryFromOldResources(p.resources)
          return {
            survivors,
            inventory,
            expeditions: [],
            craftJobs: [],
            pendingEvents: [],
            toasts: [],
            progress: typeof p.progress === 'number' ? p.progress : 0,
            locationExploration: createInitialLocationExplorationState(),
            xp: 0,
            dailyLastClaimedAt: null,
            achievementsUnlocked: [],
            achievementsClaimed: [],
            stats: {
              totalFoodCollected: 0,
              totalCraftsCompleted: 0,
              denBoschTripsCompleted: 0,
              utrechtTripsCompleted: 0,
              totalExpeditionsCompleted: 0,
              survivorsRecruited: 1,
              vehiclesFound: 0,
              hideoutMoves: 0,
              radioBuilt: false,
              totalWaterCollected: 0,
              totalInjuries: 0,
            },
            trinketPoints: 0,
            onboardingStep: hasSurvivors ? ('done' as const) : ('character_create' as const),
            settings: { ...DEFAULT_GAME_SETTINGS },
            lastSyncAt: null,
            isSyncing: false,
            syncError: null,
            hideoutTierId: 'forest_hideout' as HideoutTierId,
            workstations: newGameWorkstationStates(),
            quests: { ...DEFAULT_QUEST_STATE },
            garageVehicles: [],
            pets: [],
          }
        }
        const normalizedInventory = (p.inventory ?? []).map((stack) => ({
          ...stack,
          locked: stack.locked ?? false,
        }))
        const normalizedExpeditions = (p.expeditions ?? []).map((expedition) => {
          const ex = expedition as Expedition
          const survivorIds =
            ex.survivorIds?.length && ex.survivorIds.length > 0
              ? ex.survivorIds
              : [ex.survivorId]
          return {
            ...ex,
            survivorIds,
            survivorId: survivorIds[0] ?? ex.survivorId,
            status: expedition.status === 'returned' ? 'returned' : 'active',
          }
        })
        const merged = {
          ...p,
          inventory:
            normalizedInventory.length > 0
              ? normalizedInventory
              : MINIMAL_STARTER_INVENTORY.map((s) => ({ ...s })),
          expeditions: normalizedExpeditions,
          craftJobs: p.craftJobs ?? [],
          xp: p.xp ?? 0,
          dailyLastClaimedAt: p.dailyLastClaimedAt ?? null,
          achievementsUnlocked: p.achievementsUnlocked ?? [],
          achievementsClaimed: (p as Partial<GameState>).achievementsClaimed ?? [],
          stats: {
            totalFoodCollected: 0,
            totalCraftsCompleted: 0,
            denBoschTripsCompleted: 0,
            utrechtTripsCompleted: 0,
            totalExpeditionsCompleted: 0,
            survivorsRecruited: 1,
            vehiclesFound: 0,
            hideoutMoves: 0,
            radioBuilt: false,
            totalWaterCollected: 0,
            totalInjuries: 0,
            ...(p.stats ?? {}),
          },
          trinketPoints: p.trinketPoints ?? 0,
          onboardingStep:
            p.onboardingStep ??
            ((p.progress ?? 0) > 0 ? ('done' as const) : ('not_started' as const)),
          lastSyncAt: p.lastSyncAt ?? null,
          isSyncing: false,
          syncError: null,
          settings: normalizeRemoteSettings(
            (p as Partial<GameState>).settings ?? null,
          ),
          quests: mergeQuestRuntimeState((p as Partial<GameState>).quests),
          locationExploration: {
            ...createInitialLocationExplorationState(),
            ...(p as Partial<GameState>).locationExploration,
          },
        } as GameState
        merged.survivors = (merged.survivors ?? []).map((s) =>
          ensureSurvivorShape(s as Partial<Survivor> & { id: string }),
        )
        if (version < 9) {
          merged.hideoutTierId =
            merged.hideoutTierId ?? ('forest_hideout' as HideoutTierId)
          merged.workstations = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
          )
        }
        if (version < 10) {
          merged.workstations = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
            'legacy',
          )
          const step = merged.onboardingStep
          if (
            step === 'survivor_intro_1' ||
            step === 'survivor_intro_2' ||
            step === 'survivor_intro_3'
          ) {
            merged.onboardingStep = 'first_expedition'
          }
        }
        if (version < 11) {
          const kitchenBuilt =
            merged.workstations?.kitchen?.built ?? false
          const migrated = migrateOnboardingToQuests(
            merged.onboardingStep,
            kitchenBuilt,
          )
          if (migrated) {
            merged.onboardingStep = migrated.onboardingStep
            merged.quests = migrated.quests
          } else if (
            merged.onboardingStep === 'done' &&
            merged.quests.starterSurvival.status === 'inactive'
          ) {
            merged.quests = mergeQuestRuntimeState({
              starterSurvival: {
                status: 'completed',
                completedAt: Date.now(),
                acknowledged: true,
              },
            })
          }
        }
        if (version < 15) {
          merged.gems = 100
        }
        merged.gems = merged.gems ?? 100
        merged.quests = mergeQuestRuntimeState(merged.quests)
        const syncedAvail = reduceDenBoschGatewaySyncAvailability(
          merged.quests,
          merged.progress,
          merged.locationExploration,
        )
        if (syncedAvail) merged.quests = syncedAvail
        const cookingSyncAvail = reduceCookingSkillQuestAvailability(
          merged.quests,
          (merged.survivors ?? []).length,
          merged.inventory ?? [],
          merged.survivors ?? [],
        )
        if (cookingSyncAvail) merged.quests = cookingSyncAvail
        const hideoutMoveSyncAvail = reduceHideoutMoveQuestAvailability(
          merged.quests,
          merged.hideoutTierId ?? 'forest_hideout',
          (merged.workstations as Record<string, { level?: number }> | undefined)?.base_station?.level ?? 1,
        )
        if (hideoutMoveSyncAvail) merged.quests = hideoutMoveSyncAvail
        if (version < 15) {
          merged.garageVehicles = merged.garageVehicles ?? []
          merged.workstations = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
            'newGame',
          )
        }
        if (version < 18) {
          // Existing players skip the tutorial; new players get it via advanceOnboarding
          merged.tutorialStep = merged.tutorialStep ?? null
        }
        if (version < 20) {
          merged.workstations = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
            'newGame',
          )
          merged.garageVehicles = (merged.garageVehicles ?? [])
            .map((v) => ensureVehicleInstanceShape(v as VehicleInstance))
            .filter((v) => typeof v.id === 'string' && typeof v.defId === 'string')
        }
        if (version < 21) {
          merged.pets = merged.pets ?? []
          merged.trinketPoints = merged.trinketPoints ?? 0
          merged.stats = {
            ...merged.stats,
            utrechtTripsCompleted: merged.stats.utrechtTripsCompleted ?? 0,
          }
          merged.quests = mergeQuestRuntimeState(merged.quests)
          merged.workstations = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
            'newGame',
          )
        }
        if (version < 22) {
          merged.quests = mergeQuestRuntimeState(merged.quests)
          const wsMerged = mergeWorkstationStates(
            merged.workstations as
              | Partial<Record<WorkstationId, WorkstationRuntimeState>>
              | undefined,
            'newGame',
          )
          const ugSync = syncUtrechtGatewayPhaseFromState(
            merged.quests,
            merged.inventory ?? [],
            wsMerged,
          )
          if (ugSync) merged.quests = ugSync
        }
        return merged
      },
      partialize: (state) => ({
        survivors: state.survivors,
        inventory: state.inventory,
        expeditions: state.expeditions,
        craftJobs: state.craftJobs,
        pendingEvents: state.pendingEvents,
        toasts: [],
        progress: state.progress,
        locationExploration: state.locationExploration,
        xp: state.xp,
        dailyLastClaimedAt: state.dailyLastClaimedAt,
        achievementsUnlocked: state.achievementsUnlocked,
        achievementsClaimed: state.achievementsClaimed,
        stats: state.stats,
        trinketPoints: state.trinketPoints,
        gems: state.gems,
        onboardingStep: state.onboardingStep,
        tutorialStep: state.tutorialStep,
        settings: state.settings,
        lastSyncAt: state.lastSyncAt,
        hideoutTierId: state.hideoutTierId,
        workstations: state.workstations,
        quests: state.quests,
        garageVehicles: state.garageVehicles,
        pets: state.pets,
      }),
    },
  ),
)
